import { supabase } from "../supabase";
import {
  AttributionScope,
  RevenueAttribution,
  RevenueBillingRecord,
  RevenueCommissionPlan,
  RevenueCommissionEntry,
  RevenueOpportunity,
  UpdateRevenueOpportunityInput,
  RevenuePartner,
  RevenueProduct,
  RevenuePayoutRecord,
  RevenuePayoutItem,
  RevenueAgentTask,
  RevenueAuditLogEntry,
  RevenueStripeWebhookEvent,
  RevenueStripeMirrorHealth,
  RevenueMirrorIntegrityCheck,
  RevenueMirrorIntegrityIssueSample,
  CreateStripeMirrorReviewTaskInput,
  CreateEligibleRevenueReviewTaskInput,
  CreateCommissionReadinessReviewTaskInput,
  EligibleRevenueReviewData,
  EligibleRevenueEvent,
  EligibleRevenueCandidate,
  AccountOwnerType,
  AccountOwnershipStatus,
  RevenueAccountOwnership,
  RevenueCommissionPlanTier,
  RevenuePartnerPlanAssignment,
  SalesPartnerRelationship,
  SalesPartnerRelationshipType,
  CommissionReadinessReviewData,
  CommissionReadinessCandidate,
  CommissionReviewAction,
  RevenuePartnerUserLink,
  CreateRevenuePartnerUserLinkInput,
  UpdateRevenuePartnerUserLinkInput,
  PartnerUserLinkRole,
} from "../../types/revenueOps";
import { getAccountDisplayName, getAccountDomain, RevenueAccountProfile } from "./accountLabels";
import { deriveEligibleRevenueReviewData, EligibleRevenueRow } from "./eligibleRevenue";
import { buildCommissionEntrySourceKey, canCreateDraftCommissionEntry, deriveCommissionReadinessReviewData, CommissionReadinessRow } from "./commissionReadiness";
import { normalizeAccountOwnershipPayload } from "./identity";
import { extractStripeInvoiceLineFromPayload, mapStripeInvoiceLine } from "./stripeLineMapping";
import { buildCommissionReviewUpdate } from "./commissionReview";
import { buildPayoutBatchDraft, validatePayoutSelection, PayoutPreparationEntry } from "./payoutPreparation";
import { buildPayoutPaymentAuditMetadata, buildPayoutPaymentNotes, PayoutPaymentConfirmation, validatePayoutPaymentConfirmation } from "./payoutPaymentConfirmation";
import { DEFAULT_OWNERSHIP_WINDOW_MONTHS, deriveOwnershipExpiresAt, deriveOwnershipWindowStatus } from "./partnerOpportunityRequests";

type Row = Record<string, unknown>;

export type RevenueApiResult<T> = {
  data: T | null;
  error: Error | null;
};

export type RevenuePersistenceErrorKind = "schema" | "access" | "validation" | "conflict" | "network" | "required_field" | "unknown";

export class RevenuePersistenceError extends Error {
  kind: RevenuePersistenceErrorKind;
  code?: string;

  constructor(kind: RevenuePersistenceErrorKind, message: string, code?: string) {
    super(message);
    this.name = "RevenuePersistenceError";
    this.kind = kind;
    this.code = code;
  }
}

export type RevenuePartnerDraft = Omit<RevenuePartner, "id">;
export type RevenueOpportunityDraft = Omit<RevenueOpportunity, "id" | "partner_name"> & { partner_id?: string | null };
export type RevenueAttributionDraft = Omit<RevenueAttribution, "id" | "partner_name"> & { partner_id?: string | null };
export type RevenueProductDraft = Omit<RevenueProduct, "id">;
export type RevenueCommissionPlanDraft = Omit<RevenueCommissionPlan, "id"> & {
  tiers?: Array<{ year_start: number; year_end?: number | null; commission_rate: number; requires_active_business: boolean }>;
};
export type RevenueAccountOwnershipDraft = Omit<RevenueAccountOwnership, "id" | "owner_partner_name" | "updated_at">;
export type RevenueAccountDirectory = Map<string, RevenueAccountProfile>;

function text(row: Row, key: string, fallback = "") {
  const value = row[key];
  return value === null || value === undefined ? fallback : String(value);
}

function bool(row: Row, key: string, fallback = false) {
  return row[key] === undefined || row[key] === null ? fallback : Boolean(row[key]);
}

function number(row: Row, key: string) {
  const value = Number(row[key]);
  return Number.isFinite(value) ? value : undefined;
}

function toPersistenceError(error: { message?: string; code?: string; status?: number } | null, fallback: string) {
  const code = error?.code;
  const message = error?.message || fallback;
  let kind: RevenuePersistenceErrorKind = "unknown";
  if (code === "42P01" || code === "42703" || /schema|relation|column|does not exist/i.test(message)) kind = "schema";
  else if (code === "42501" || /permission|row-level security|RLS|policy/i.test(message)) kind = "access";
  else if (error?.status === 409 || code === "23505" || /duplicate|unique constraint|already exists|conflict|\b409\b/i.test(message)) kind = "conflict";
  else if (error?.status === 400 || code === "22P02" || code === "23514" || /check constraint|invalid input|violates/i.test(message)) kind = "validation";
  else if (code === "23502" || /not-null|required/i.test(message)) kind = "required_field";
  else if (/network|fetch|timeout|connection/i.test(message)) kind = "network";
  return new RevenuePersistenceError(kind, `${kind}: ${message}`, code);
}

async function listRows(table: string): Promise<RevenueApiResult<Row[]>> {
  const { data, error } = await supabase.from(table).select("*").order("created_at", { ascending: false });
  return { data: (data || []) as Row[], error: error ? toPersistenceError(error, `Could not read ${table}`) : null };
}

async function createRow(table: string, payload: Row): Promise<RevenueApiResult<Row>> {
  const { data, error } = await supabase.from(table).insert(payload).select("*").single();
  return { data: (data || null) as Row | null, error: error ? toPersistenceError(error, `Could not write ${table}`) : null };
}

async function updateRow(table: string, id: string, payload: Row): Promise<RevenueApiResult<Row>> {
  const { data, error } = await supabase.from(table).update(payload).eq("id", id).select("*").single();
  return { data: (data || null) as Row | null, error: error ? toPersistenceError(error, `Could not update ${table}`) : null };
}

export async function listRevenueAccountProfiles(): Promise<RevenueApiResult<RevenueAccountProfile[]>> {
  const { data, error } = await supabase
    .from("accounts")
    .select("account_id, display_name, business_name, account_name, website_domain, website_url");
  return { data: (data || []) as RevenueAccountProfile[], error: error ? toPersistenceError(error, "Could not read account names") : null };
}

export async function listRevenuePartners(): Promise<RevenueApiResult<RevenuePartner[]>> {
  const result = await listRows("sales_partners");
  if (result.error) return { data: null, error: result.error };
  return {
    data: (result.data || []).map(mapPartner),
    error: null,
  };
}

const partnerUserRoleToDb: Record<PartnerUserLinkRole, "owner" | "member" | "finance" | "viewer"> = {
  owner: "owner", member: "member", finance: "finance", viewer: "viewer",
  partner_admin: "owner", partner_member: "member", partner_finance: "finance", partner_read_only: "viewer",
};

const partnerUserRoleFromDb: Record<string, PartnerUserLinkRole> = {
  owner: "partner_admin",
  member: "partner_member",
  finance: "partner_finance",
  viewer: "partner_read_only",
  partner_admin: "partner_admin",
  partner_member: "partner_member",
  partner_finance: "partner_finance",
  partner_read_only: "partner_read_only",
};

function mapRevenuePartnerUserLink(row: Row, partnerById = new Map<string, string>()): RevenuePartnerUserLink {
  return {
    id: text(row, "id"),
    partner_id: text(row, "partner_id"),
    user_id: text(row, "user_id"),
    role: partnerUserRoleFromDb[text(row, "role", "member")] || "member",
    is_active: bool(row, "is_active", true),
    created_at: text(row, "created_at") || undefined,
    updated_at: text(row, "updated_at") || undefined,
    notes: text(row, "notes") || undefined,
    user_email: text(row, "user_email") || undefined,
    user_name: text(row, "user_name") || undefined,
    partner_name: partnerById.get(text(row, "partner_id")),
  };
}

export async function listRevenuePartnerUserLinks(partnerById = new Map<string, string>()): Promise<RevenueApiResult<RevenuePartnerUserLink[]>> {
  const result = await listRows("sales_partner_users");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => mapRevenuePartnerUserLink(row, partnerById)), error: null };
}

function validatePartnerUserLinkPayload(input: CreateRevenuePartnerUserLinkInput | UpdateRevenuePartnerUserLinkInput, partner?: RevenuePartner): RevenuePersistenceError | null {
  if ("partner_id" in input && !String(input.partner_id || "").trim()) return new RevenuePersistenceError("required_field", "required_field: Select a partner");
  if ("user_id" in input && !String(input.user_id || "").trim()) return new RevenuePersistenceError("required_field", "required_field: Enter an existing auth/account user ID");
  if (input.role && !partnerUserRoleToDb[input.role]) return new RevenuePersistenceError("validation", "validation: Select a valid partner access role");
  if (partner && partner.status !== "active" && !(input as CreateRevenuePartnerUserLinkInput).allow_inactive_partner) return new RevenuePersistenceError("validation", "validation: The selected partner is inactive. Confirm before linking a user.");
  return null;
}

export async function createRevenuePartnerUserLink(input: CreateRevenuePartnerUserLinkInput, partners: RevenuePartner[] = []): Promise<RevenueApiResult<RevenuePartnerUserLink>> {
  const partner = partners.find((item) => item.id === input.partner_id);
  const validationError = validatePartnerUserLinkPayload(input, partner);
  if (validationError) return { data: null, error: validationError };
  const existing = await listRevenuePartnerUserLinks(new Map(partners.map((item) => [item.id, item.display_name])));
  if (existing.error) return { data: null, error: existing.error };
  const same = (existing.data || []).find((link) => link.partner_id === input.partner_id && link.user_id === input.user_id);
  if (same?.is_active) return { data: null, error: new RevenuePersistenceError("conflict", "conflict: This user is already actively linked to this partner.") };
  if (same && !input.is_active) return { data: same, error: null };
  if (same) return updateRevenuePartnerUserLink(same.id, { role: input.role, is_active: true, notes: input.notes }, partners);
  const result = await createRow("sales_partner_users", { partner_id: input.partner_id, user_id: input.user_id, role: partnerUserRoleToDb[input.role], is_active: input.is_active !== false });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner user link was not created") };
  const mapped = mapRevenuePartnerUserLink(result.data, new Map(partners.map((item) => [item.id, item.display_name])));
  await writeRevenueAudit("partner_user_link_created", "sales_partner_user", mapped.id, null, { partner_id: mapped.partner_id, user_id: mapped.user_id, role: mapped.role, old_status: null, new_status: mapped.is_active ? "active" : "inactive" }, "Existing user linked to partner");
  return { data: mapped, error: null };
}

export async function updateRevenuePartnerUserLink(id: string, input: UpdateRevenuePartnerUserLinkInput, partners: RevenuePartner[] = []): Promise<RevenueApiResult<RevenuePartnerUserLink>> {
  const validationError = validatePartnerUserLinkPayload(input);
  if (validationError) return { data: null, error: validationError };
  if (input.is_active) {
    const existing = await listRevenuePartnerUserLinks();
    if (existing.error) return { data: null, error: existing.error };
    const current = existing.data?.find((item) => item.id === id);
    const duplicate = existing.data?.find((item) => item.id !== id && item.is_active && item.partner_id === current?.partner_id && item.user_id === current?.user_id);
    if (duplicate) return { data: null, error: new RevenuePersistenceError("conflict", "conflict: This user is already actively linked to this partner.") };
  }
  const payload: Row = {};
  if (input.role) payload.role = partnerUserRoleToDb[input.role];
  if (input.is_active !== undefined) payload.is_active = input.is_active;
  const result = await updateRow("sales_partner_users", id, payload);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner user link was not updated") };
  const mapped = mapRevenuePartnerUserLink(result.data, new Map(partners.map((item) => [item.id, item.display_name])));
  await writeRevenueAudit(mapped.is_active ? "partner_user_link_reactivated" : "partner_user_link_deactivated", "sales_partner_user", id, { partner_id: mapped.partner_id, user_id: mapped.user_id, role: mapped.role, old_status: !mapped.is_active ? "active" : "inactive" }, { partner_id: mapped.partner_id, user_id: mapped.user_id, role: mapped.role, new_status: mapped.is_active ? "active" : "inactive" }, "Partner user link status changed");
  return { data: mapped, error: null };
}

export async function setRevenuePartnerUserLinkActive(id: string, isActive: boolean, partners: RevenuePartner[] = []): Promise<RevenueApiResult<RevenuePartnerUserLink>> {
  return updateRevenuePartnerUserLink(id, { is_active: isActive }, partners);
}

function mapPartner(row: Row): RevenuePartner {
  return {
    id: text(row, "id"),
    legal_name: text(row, "legal_name") || undefined,
    display_name: text(row, "display_name", text(row, "legal_name")),
    partner_type: mapPartnerType(text(row, "partner_type")),
    status: mapPartnerStatus(text(row, "status")),
    agreement_version: text(row, "agreement_version"),
    payout_terms: mapPayoutTerms(text(row, "default_payout_terms")),
    attribution_scope: text(row, "attribution_scope", "account_only") as AttributionScope,
    contact_email: text(row, "contact_email") || undefined,
    external_reference: text(row, "external_reference") || undefined,
    payment_hold_reason: text(row, "payment_hold_reason") || undefined,
    year_three_eligibility: (text(row, "year_three_eligibility", "not_applicable") as RevenuePartner["year_three_eligibility"]),
    relationship_summary: text(row, "relationship_summary") || undefined,
    notes: text(row, "notes") || undefined,
    tax_profile_status: text(row, "tax_profile_status") as RevenuePartner["tax_profile_status"],
    tax_classification: text(row, "tax_classification") || undefined,
    tax_legal_name: text(row, "tax_legal_name") || undefined,
    tax_address_summary: text(row, "tax_address_summary") || undefined,
    tax_tin_last4: text(row, "tax_tin_last4") || undefined,
    w9_received_at: text(row, "w9_received_at") || text(row, "tax_form_received_at") || undefined,
    tax_notes: text(row, "tax_notes") || undefined,
  };
}

export async function createRevenuePartner(draft: RevenuePartnerDraft): Promise<RevenueApiResult<RevenuePartner>> {
  const result = await createRow("sales_partners", {
    legal_name: (draft.legal_name || draft.display_name).trim(),
    display_name: draft.display_name.trim(),
    partner_type: mapPartnerTypeToDb(draft.partner_type),
    status: mapPartnerStatusToDb(draft.status),
    default_payout_terms: draft.payout_terms,
    agreement_version: draft.agreement_version || null,
    payment_hold_reason: draft.payment_hold_reason || null,
    year_three_eligibility: draft.year_three_eligibility,
    attribution_scope: draft.attribution_scope || "account_only",
    contact_email: draft.contact_email || null,
    external_reference: draft.external_reference || null,
    relationship_summary: draft.relationship_summary || null,
    notes: draft.notes || null,
  });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner was not created") };
  await writeRevenueAudit("partner_created", "sales_partner", text(result.data, "id"), null, result.data);
  return { data: mapPartner(result.data), error: null };
}

export async function updateRevenuePartnerStatus(id: string, status: RevenuePartner["status"]): Promise<RevenueApiResult<Row>> {
  const result = await updateRow("sales_partners", id, { status: mapPartnerStatusToDb(status) });
  if (!result.error) await writeRevenueAudit("partner_status_changed", "sales_partner", id, null, { status });
  return result;
}

export async function updateRevenuePartner(id: string, draft: RevenuePartnerDraft): Promise<RevenueApiResult<RevenuePartner>> {
  if (!draft.display_name.trim()) return { data: null, error: new RevenuePersistenceError("required_field", "required_field: Partner name is required") };
  const result = await updateRow("sales_partners", id, {
    legal_name: (draft.legal_name || draft.display_name).trim(), display_name: draft.display_name.trim(),
    partner_type: mapPartnerTypeToDb(draft.partner_type), status: mapPartnerStatusToDb(draft.status),
    attribution_scope: draft.attribution_scope || "account_only", default_payout_terms: draft.payout_terms,
    contact_email: draft.contact_email || null, external_reference: draft.external_reference || null,
    agreement_version: draft.agreement_version || null,
    payment_hold_reason: draft.payment_hold_reason || null, year_three_eligibility: draft.year_three_eligibility,
    relationship_summary: draft.relationship_summary || null, notes: draft.notes || null,
  });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner was not updated") };
  await writeRevenueAudit("partner_updated", "sales_partner", id, null, result.data, "Sales partner updated");
  return { data: mapPartner(result.data), error: null };
}

export type CreatePartnerPlanAssignmentInput = {
  partner_id: string;
  plan_id: string;
  payout_terms: RevenuePartner["payout_terms"];
  starts_at: string;
  ends_at?: string | null;
  admin_notes?: string | null;
};
export type UpdatePartnerPlanAssignmentInput = Partial<Omit<CreatePartnerPlanAssignmentInput, "partner_id" | "plan_id">>;

export type CreateSalesPartnerRelationshipInput = {
  parent_partner_id: string;
  child_partner_id: string;
  relationship_type: SalesPartnerRelationshipType;
  override_rate: number;
  starts_at: string;
  ends_at?: string | null;
  is_active?: boolean;
  notes?: string | null;
};
export type UpdateSalesPartnerRelationshipInput = Partial<Omit<CreateSalesPartnerRelationshipInput, "parent_partner_id" | "child_partner_id">> & {
  parent_partner_id?: string;
  child_partner_id?: string;
};

export function mapPartnerRelationshipRow(row: Row): SalesPartnerRelationship {
  return {
    id: text(row, "id"),
    parent_partner_id: text(row, "parent_partner_id"),
    child_partner_id: text(row, "child_partner_id"),
    relationship_type: text(row, "relationship_type", "other") as SalesPartnerRelationshipType,
    override_rate: number(row, "override_rate") ?? 0,
    starts_at: text(row, "starts_at"),
    ends_at: text(row, "ends_at") || undefined,
    is_active: bool(row, "is_active", true),
    notes: text(row, "notes") || undefined,
  };
}

export async function listPartnerRelationships(): Promise<RevenueApiResult<SalesPartnerRelationship[]>> {
  const result = await listRows("sales_partner_relationships");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map(mapPartnerRelationshipRow), error: null };
}

export async function listAdvisoryOverrideRelationships(): Promise<RevenueApiResult<SalesPartnerRelationship[]>> {
  const result = await listPartnerRelationships();
  return { data: result.data?.filter((relationship) => relationship.relationship_type === "advisory_override") || null, error: result.error };
}

function validatePartnerRelationshipInput(input: CreateSalesPartnerRelationshipInput | UpdateSalesPartnerRelationshipInput) {
  if (input.relationship_type !== undefined && input.relationship_type !== "advisory_override") return new RevenuePersistenceError("validation", "validation: Only advisory_override relationships are supported by this workflow");
  if (input.parent_partner_id && input.child_partner_id && input.parent_partner_id === input.child_partner_id) return new RevenuePersistenceError("validation", "validation: Advisor and child partner must be different");
  if (input.override_rate !== undefined && (input.override_rate < 0 || input.override_rate > 1)) return new RevenuePersistenceError("validation", "validation: Override rate must be between 0 and 1");
  if (input.starts_at && input.ends_at && input.ends_at < input.starts_at) return new RevenuePersistenceError("validation", "validation: End date must be on or after the start date");
  return null;
}

function isActivePartnerRelationship(relationship: SalesPartnerRelationship, today = new Date().toISOString().slice(0, 10)) {
  return relationship.is_active && relationship.starts_at <= today && (!relationship.ends_at || relationship.ends_at >= today);
}

async function ensureNoDuplicatePartnerRelationship(input: CreateSalesPartnerRelationshipInput | UpdateSalesPartnerRelationshipInput, excludeId?: string) {
  if (!input.parent_partner_id || !input.child_partner_id || input.relationship_type !== "advisory_override") return null;
  const existing = await listPartnerRelationships();
  if (existing.error) return existing.error;
  const duplicate = (existing.data || []).some((relationship) => relationship.id !== excludeId && relationship.relationship_type === "advisory_override" && relationship.parent_partner_id === input.parent_partner_id && relationship.child_partner_id === input.child_partner_id && isActivePartnerRelationship(relationship));
  return duplicate ? new RevenuePersistenceError("conflict", "conflict: This advisory override relationship already exists. Edit the existing relationship instead.") : null;
}

export async function createPartnerRelationship(input: CreateSalesPartnerRelationshipInput): Promise<RevenueApiResult<SalesPartnerRelationship>> {
  if (!input.parent_partner_id || !input.child_partner_id || !input.starts_at) return { data: null, error: new RevenuePersistenceError("required_field", "required_field: Advisor, child partner, and start date are required") };
  const validationError = validatePartnerRelationshipInput(input);
  if (validationError) return { data: null, error: validationError };
  const duplicateError = await ensureNoDuplicatePartnerRelationship(input);
  if (duplicateError) return { data: null, error: duplicateError };
  const result = await createRow("sales_partner_relationships", { ...input, ends_at: input.ends_at || null, is_active: input.is_active ?? true, notes: input.notes || null });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner relationship was not created") };
  await writeRevenueAudit("advisory_override_created", "sales_partner_relationship", text(result.data, "id"), null, result.data, "Advisory override relationship created");
  return { data: mapPartnerRelationshipRow(result.data), error: null };
}

export async function updatePartnerRelationship(id: string, input: UpdateSalesPartnerRelationshipInput): Promise<RevenueApiResult<SalesPartnerRelationship>> {
  const validationError = validatePartnerRelationshipInput(input);
  if (validationError) return { data: null, error: validationError };
  const duplicateError = await ensureNoDuplicatePartnerRelationship(input, id);
  if (duplicateError) return { data: null, error: duplicateError };
  const result = await updateRow("sales_partner_relationships", id, { ...input, ends_at: input.ends_at || null });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner relationship was not updated") };
  await writeRevenueAudit("advisory_override_updated", "sales_partner_relationship", id, null, result.data, "Advisory override relationship updated");
  return { data: mapPartnerRelationshipRow(result.data), error: null };
}

export async function endPartnerRelationship(id: string, endsAt = new Date().toISOString().slice(0, 10)): Promise<RevenueApiResult<SalesPartnerRelationship>> {
  const result = await updateRow("sales_partner_relationships", id, { ends_at: endsAt, is_active: false });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner relationship was not ended") };
  await writeRevenueAudit("advisory_override_ended", "sales_partner_relationship", id, null, result.data, "Advisory override relationship ended");
  return { data: mapPartnerRelationshipRow(result.data), error: null };
}

function mapPartnerPlanAssignment(row: Row, planDirectory: Map<string, RevenueCommissionPlan>): RevenuePartnerPlanAssignment {
  const planId = text(row, "plan_id");
  const plan = planDirectory.get(planId);
  return { id: text(row, "id"), partner_id: text(row, "partner_id"), plan_id: planId, plan_name: plan?.plan_name, plan_type: plan?.plan_type, payout_terms: mapPayoutTerms(text(row, "payout_terms")), starts_at: text(row, "starts_at"), ends_at: text(row, "ends_at") || undefined, admin_notes: text(row, "admin_notes") || undefined, tiers: plan?.tiers };
}

export async function listPartnerPlanAssignments(planDirectory: Map<string, RevenueCommissionPlan> = new Map()): Promise<RevenueApiResult<RevenuePartnerPlanAssignment[]>> {
  const result = await listRows("partner_plan_assignments");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => mapPartnerPlanAssignment(row, planDirectory)), error: null };
}

export function getActivePartnerPlanAssignment(assignments: RevenuePartnerPlanAssignment[], partnerId: string, today = new Date().toISOString().slice(0, 10)) {
  return assignments.filter((item) => item.partner_id === partnerId && item.starts_at <= today && (!item.ends_at || item.ends_at >= today)).sort((a, b) => b.starts_at.localeCompare(a.starts_at))[0];
}

export async function createPartnerPlanAssignment(input: CreatePartnerPlanAssignmentInput, planDirectory: Map<string, RevenueCommissionPlan> = new Map()): Promise<RevenueApiResult<RevenuePartnerPlanAssignment>> {
  if (!input.partner_id || !input.plan_id || !input.starts_at) return { data: null, error: new RevenuePersistenceError("required_field", "required_field: Partner, plan, and effective start are required") };
  const result = await createRow("partner_plan_assignments", { partner_id: input.partner_id, plan_id: input.plan_id, payout_terms: input.payout_terms, starts_at: input.starts_at, ends_at: input.ends_at || null, admin_notes: input.admin_notes || null });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner commission plan assignment was not created") };
  await writeRevenueAudit("partner_plan_assigned", "partner_plan_assignment", text(result.data, "id"), null, { partner_id: input.partner_id, plan_id: input.plan_id, payout_terms: input.payout_terms, starts_at: input.starts_at, ends_at: input.ends_at || null }, "Partner commission plan assigned");
  return { data: mapPartnerPlanAssignment(result.data, planDirectory), error: null };
}

export async function updatePartnerPlanAssignment(id: string, input: UpdatePartnerPlanAssignmentInput, planDirectory: Map<string, RevenueCommissionPlan> = new Map()): Promise<RevenueApiResult<RevenuePartnerPlanAssignment>> {
  const result = await updateRow("partner_plan_assignments", id, { ...input, ends_at: input.ends_at || null });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner commission plan assignment was not updated") };
  await writeRevenueAudit("partner_plan_changed", "partner_plan_assignment", id, null, result.data, "Partner commission plan assignment changed");
  return { data: mapPartnerPlanAssignment(result.data, planDirectory), error: null };
}

export async function endPartnerPlanAssignment(id: string, endsAt = new Date().toISOString().slice(0, 10)): Promise<RevenueApiResult<RevenuePartnerPlanAssignment>> {
  const result = await updateRow("partner_plan_assignments", id, { ends_at: endsAt });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Partner commission plan assignment was not ended") };
  await writeRevenueAudit("partner_plan_ended", "partner_plan_assignment", id, null, result.data, "Partner commission plan assignment ended");
  return { data: mapPartnerPlanAssignment(result.data, new Map()), error: null };
}

export async function assignPartnerCommissionPlan(input: CreatePartnerPlanAssignmentInput, planDirectory: Map<string, RevenueCommissionPlan> = new Map()): Promise<RevenueApiResult<RevenuePartnerPlanAssignment>> {
  const existing = await listPartnerPlanAssignments(planDirectory);
  if (existing.error) return { data: null, error: existing.error };
  const current = getActivePartnerPlanAssignment(existing.data || [], input.partner_id, input.starts_at);
  if (current && current.plan_id === input.plan_id && current.payout_terms === input.payout_terms && current.starts_at === input.starts_at && (current.ends_at || null) === (input.ends_at || null)) return { data: current, error: null };
  if (current) {
    const replacementStart = new Date(`${input.starts_at}T00:00:00Z`);
    replacementStart.setUTCDate(replacementStart.getUTCDate() - 1);
    const ended = await endPartnerPlanAssignment(current.id, replacementStart.toISOString().slice(0, 10));
    if (ended.error) return { data: null, error: ended.error };
  }
  return createPartnerPlanAssignment(input, planDirectory);
}

export async function listRevenueOpportunities(partnerById: Map<string, string>, accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<RevenueOpportunity[]>> {
  const result = await listRows("sales_opportunities");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => mapOpportunity(row, partnerById, accountDirectory)), error: null };
}

function mapAccountOwnership(row: Row, partnerById: Map<string, string>, accountDirectory: RevenueAccountDirectory = new Map()): RevenueAccountOwnership {
  const accountId = text(row, "account_id");
  const account = accountDirectory.get(accountId);
  return {
    id: text(row, "id"),
    account_id: accountId,
    account_name: account ? getAccountDisplayName(account) : "Account mapping needs review",
    account_mapping_issue: Boolean(accountId && !account),
    account_domain: account ? getAccountDomain(account) : undefined,
    owner_type: text(row, "owner_type", "unassigned") as AccountOwnerType,
    owner_partner_id: text(row, "owner_partner_id") || undefined,
    owner_partner_name: partnerById.get(text(row, "owner_partner_id")) || undefined,
    ownership_status: text(row, "ownership_status", "review_needed") as AccountOwnershipStatus,
    visibility: text(row, "visibility", "internal_only") as RevenueAccountOwnership["visibility"],
    effective_start: text(row, "effective_start"),
    effective_end: text(row, "effective_end") || undefined,
    internal_notes: text(row, "internal_notes") || undefined,
    partner_visible_note: text(row, "partner_visible_note") || undefined,
    updated_at: text(row, "updated_at") || undefined,
  };
}

export async function listAccountOwnerships(partnerById: Map<string, string> = new Map(), accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<RevenueAccountOwnership[]>> {
  const result = await listRows("revenue_account_ownerships");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => mapAccountOwnership(row, partnerById, accountDirectory)), error: null };
}

export async function createAccountOwnership(draft: RevenueAccountOwnershipDraft): Promise<RevenueApiResult<RevenueAccountOwnership>> {
  const normalized = normalizeAccountOwnershipPayload(draft as unknown as Record<string, unknown>, true);
  if (normalized.error) return { data: null, error: new RevenuePersistenceError("validation", `validation: ${normalized.error}`) };
  const user = await supabase.auth.getUser();
  const result = await createRow("revenue_account_ownerships", { ...normalized.payload, created_by: user.data.user?.id || null, updated_by: user.data.user?.id || null });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Account ownership was not created") };
  await writeRevenueAudit("account_ownership_created", "revenue_account_ownership", text(result.data, "id"), null, result.data);
  return { data: mapAccountOwnership(result.data, new Map()), error: null };
}

export async function updateAccountOwnership(id: string, draft: Partial<RevenueAccountOwnershipDraft>): Promise<RevenueApiResult<RevenueAccountOwnership>> {
  const normalized = normalizeAccountOwnershipPayload(draft as unknown as Record<string, unknown>);
  if (normalized.error) return { data: null, error: new RevenuePersistenceError("validation", `validation: ${normalized.error}`) };
  const user = await supabase.auth.getUser();
  const payload: Row = { ...(normalized.payload || {}), updated_by: user.data.user?.id || null };
  const result = await updateRow("revenue_account_ownerships", id, payload);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Account ownership was not updated") };
  const action = draft.owner_type ? "account_owner_changed" : draft.visibility ? "account_ownership_visibility_changed" : draft.ownership_status ? "account_ownership_status_changed" : "account_ownership_changed";
  await writeRevenueAudit(action, "revenue_account_ownership", id, null, result.data);
  return { data: mapAccountOwnership(result.data, new Map()), error: null };
}

export async function setAccountOwner(id: string, ownerType: AccountOwnerType, ownerPartnerId?: string | null): Promise<RevenueApiResult<RevenueAccountOwnership>> {
  return updateAccountOwnership(id, { owner_type: ownerType, owner_partner_id: ownerPartnerId || undefined, ownership_status: ownerType === "unassigned" ? "review_needed" : "active" });
}

export async function markAccountOwnershipDisputed(id: string): Promise<RevenueApiResult<RevenueAccountOwnership>> {
  const result = await updateAccountOwnership(id, { ownership_status: "disputed" });
  if (!result.error) await writeRevenueAudit("account_ownership_marked_disputed", "revenue_account_ownership", id, null, result.data);
  return result;
}

export async function endAccountOwnership(id: string, effectiveEnd = new Date().toISOString().slice(0, 10)): Promise<RevenueApiResult<RevenueAccountOwnership>> {
  const result = await updateAccountOwnership(id, { ownership_status: "ended", effective_end: effectiveEnd });
  if (!result.error) await writeRevenueAudit("account_ownership_ended", "revenue_account_ownership", id, null, result.data);
  return result;
}

export async function createRevenueOpportunity(draft: RevenueOpportunityDraft): Promise<RevenueApiResult<RevenueOpportunity>> {
  const ownershipExpiresAt = deriveOwnershipExpiresAt(draft.last_meaningful_activity_at, Number(draft.ownership_window_months || DEFAULT_OWNERSHIP_WINDOW_MONTHS));
  const result = await createRow("sales_opportunities", {
    partner_id: draft.partner_id || null,
    account_id: draft.account_id || null,
    referred_entity_name: draft.prospect_name.trim(),
    attribution_scope: draft.attribution_scope,
    stage: mapOpportunityStageToDb(draft.stage),
    source: "admin_manual",
    notes: draft.notes || null,
    metadata: { website: draft.website || null, contact_name: draft.contact_name || null, contact_email: draft.contact_email || null, estimated_mrr: draft.estimated_mrr || null, opportunity_type: draft.opportunity_type, attribution_review: draft.attribution_review, parent_entity: draft.parent_entity_label?.trim() || null, last_meaningful_activity_at: draft.last_meaningful_activity_at || null, last_meaningful_activity_note: draft.last_meaningful_activity_note?.trim() || null, ownership_window_months: draft.ownership_window_months || DEFAULT_OWNERSHIP_WINDOW_MONTHS, ownership_expires_at: ownershipExpiresAt || null, ownership_window_status: deriveOwnershipWindowStatus(ownershipExpiresAt) },
  });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Opportunity was not created") };
  await writeRevenueAudit("opportunity_created", "sales_opportunity", text(result.data, "id"), null, result.data);
  return { data: mapOpportunity(result.data, new Map()), error: null };
}

export async function updateRevenueOpportunityStage(id: string, stage: RevenueOpportunity["stage"]): Promise<RevenueApiResult<Row>> {
  const result = await updateRow("sales_opportunities", id, { stage: mapOpportunityStageToDb(stage) });
  if (!result.error) await writeRevenueAudit("opportunity_stage_changed", "sales_opportunity", id, null, { stage });
  return result;
}

export async function updateRevenueOpportunity(id: string, input: UpdateRevenueOpportunityInput): Promise<RevenueApiResult<RevenueOpportunity>> {
  const existing = await supabase.from("sales_opportunities").select("*").eq("id", id).single();
  if (existing.error || !existing.data) return { data: null, error: toPersistenceError(existing.error, "Could not read opportunity before updating") };
  const existingMetadata = existing.data.metadata && typeof existing.data.metadata === "object" ? { ...(existing.data.metadata as Row) } : {};
  const parentEntity = input.parent_entity_label?.trim() || null;
  if (parentEntity) existingMetadata.parent_entity = parentEntity;
  else delete existingMetadata.parent_entity;
  const metadata: Row = { ...existingMetadata, estimated_mrr: input.estimated_mrr ?? existingMetadata.estimated_mrr, opportunity_type: input.opportunity_type || existingMetadata.opportunity_type, attribution_review: input.attribution_review || existingMetadata.attribution_review, website: input.website?.trim() || existingMetadata.website, contact_name: input.contact_name?.trim() || existingMetadata.contact_name, contact_email: input.contact_email?.trim() || existingMetadata.contact_email };
  if (!parentEntity) delete metadata.parent_entity;
  if (input.last_meaningful_activity_at !== undefined) {
    const activity = input.last_meaningful_activity_at?.trim() || "";
    const months = Number(input.ownership_window_months || DEFAULT_OWNERSHIP_WINDOW_MONTHS);
    if (activity) {
      metadata.last_meaningful_activity_at = activity;
      metadata.ownership_window_months = months;
      metadata.ownership_expires_at = deriveOwnershipExpiresAt(activity, months) || null;
      metadata.ownership_window_status = deriveOwnershipWindowStatus(metadata.ownership_expires_at as string);
    } else {
      delete metadata.last_meaningful_activity_at;
      delete metadata.ownership_window_months;
      delete metadata.ownership_expires_at;
      delete metadata.ownership_window_status;
    }
  }
  if (input.last_meaningful_activity_note !== undefined) {
    const note = input.last_meaningful_activity_note?.trim() || "";
    if (note) metadata.last_meaningful_activity_note = note;
    else delete metadata.last_meaningful_activity_note;
  }
  const payload: Row = { referred_entity_name: input.prospect_name !== undefined ? input.prospect_name.trim() : existing.data.referred_entity_name, account_id: input.account_id !== undefined ? input.account_id || null : existing.data.account_id, partner_id: input.partner_id !== undefined ? input.partner_id || null : existing.data.partner_id, stage: input.stage ? mapOpportunityStageToDb(input.stage) : existing.data.stage, attribution_scope: input.attribution_scope || existing.data.attribution_scope, notes: input.notes !== undefined ? input.notes.trim() || null : existing.data.notes, metadata };
  const result = await updateRow("sales_opportunities", id, payload);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Opportunity was not updated") };
  await writeRevenueAudit("opportunity_updated", "sales_opportunity", id, existing.data, result.data, "Opportunity updated by internal admin");
  return { data: mapOpportunity(result.data, new Map()), error: null };
}

export async function archiveRevenueOpportunity(id: string, reason = "Archived by internal admin"): Promise<RevenueApiResult<RevenueOpportunity>> {
  const existing = await supabase.from("sales_opportunities").select("*").eq("id", id).single();
  if (existing.error || !existing.data) return { data: null, error: toPersistenceError(existing.error, "Could not read opportunity before archiving") };
  const metadata = existing.data.metadata && typeof existing.data.metadata === "object" ? { ...(existing.data.metadata as Row) } : {};
  const user = await supabase.auth.getUser();
  metadata.archived_at = new Date().toISOString();
  metadata.archived_by = user.data.user?.id || null;
  metadata.archive_reason = reason.trim() || "Archived by internal admin";
  metadata.archived_from_stage = existing.data.stage || null;
  const result = await updateRow("sales_opportunities", id, { metadata });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Opportunity could not be archived") };
  await writeRevenueAudit("opportunity_archived", "sales_opportunity", id, existing.data, result.data, "Opportunity archived from active workflows");
  return { data: mapOpportunity(result.data, new Map()), error: null };
}

export async function restoreRevenueOpportunity(id: string): Promise<RevenueApiResult<RevenueOpportunity>> {
  const existing = await supabase.from("sales_opportunities").select("*").eq("id", id).single();
  if (existing.error || !existing.data) return { data: null, error: toPersistenceError(existing.error, "Could not read opportunity before restoring") };
  const metadata = existing.data.metadata && typeof existing.data.metadata === "object" ? { ...(existing.data.metadata as Row) } : {};
  delete metadata.archived_at;
  delete metadata.archived_by;
  delete metadata.archive_reason;
  delete metadata.archived_from_stage;
  const result = await updateRow("sales_opportunities", id, { metadata });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Opportunity could not be restored") };
  await writeRevenueAudit("opportunity_restored", "sales_opportunity", id, existing.data, result.data, "Opportunity restored to active workflows");
  return { data: mapOpportunity(result.data, new Map()), error: null };
}

export async function reviewRevenueOpportunityRequest(id: string, action: "approve" | "reject" | "duplicate" | "needs_info"): Promise<RevenueApiResult<RevenueOpportunity>> {
  const existing = await supabase.from("sales_opportunities").select("*").eq("id", id).single();
  if (existing.error || !existing.data) return { data: null, error: toPersistenceError(existing.error, "Could not read opportunity request") };
  const metadata = existing.data.metadata && typeof existing.data.metadata === "object" ? { ...(existing.data.metadata as Row) } : {};
  const requestType = text(metadata, "request_type");
  metadata.request_status = action === "approve" ? "approved" : action;
  const payload: Row = { metadata };
  if (action === "approve" && requestType === "claim_request" && typeof metadata.claim_request_partner_id === "string") payload.partner_id = metadata.claim_request_partner_id;
  if (action === "duplicate") payload.stage = "duplicate";
  const result = await updateRow("sales_opportunities", id, payload);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Opportunity request was not updated") };
  await writeRevenueAudit(`partner_opportunity_request_${action}`, "sales_opportunity", id, existing.data, result.data, `Partner opportunity request ${action}`);
  return { data: mapOpportunity(result.data, new Map()), error: null };
}

export async function listRevenueAttributions(partnerById: Map<string, string>, accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<RevenueAttribution[]>> {
  const result = await listRows("customer_attributions");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => mapAttribution(row, partnerById, accountDirectory)), error: null };
}

function mapAttribution(row: Row, partnerById: Map<string, string>, accountDirectory: RevenueAccountDirectory): RevenueAttribution {
  const accountId = text(row, "account_id");
  const account = accountDirectory.get(accountId);
  return { id: text(row, "id"), account_id: accountId, partner_id: text(row, "partner_id") || undefined, partner_name: partnerById.get(text(row, "partner_id")) || "Unknown partner", customer_name: account ? getAccountDisplayName(account) : "Account mapping needs review", account_mapping_issue: Boolean(accountId && !account), entity_or_domain: text(row, "scope_key"), attribution_scope: text(row, "attribution_scope") as AttributionScope, status: mapAttributionStatus(text(row, "status")), effective_start: text(row, "effective_start", text(row, "created_at").slice(0, 10)), effective_end: text(row, "effective_end") || undefined, commission_start_date: text(row, "commission_start_date"), notes: text(row, "notes") || undefined };
}

function attributionPayload(draft: RevenueAttributionDraft): Row | RevenuePersistenceError {
  const accountId = String(draft.customer_name || "").trim();
  const partnerId = String(draft.partner_id || "").trim();
  const effectiveStart = String(draft.effective_start || "").trim();
  const effectiveEnd = String(draft.effective_end || "").trim() || null;
  if (!accountId) return new RevenuePersistenceError("required_field", "required_field: Select an existing Toren account");
  if (!partnerId) return new RevenuePersistenceError("required_field", "required_field: Select a partner");
  if (!effectiveStart) return new RevenuePersistenceError("required_field", "required_field: Select an effective start date");
  if (effectiveEnd && effectiveEnd < effectiveStart) return new RevenuePersistenceError("validation", "validation: Effective end must be on or after effective start");
  const validScopes = ["account_only", "division", "legal_entity", "parent_entity", "custom"];
  if (!validScopes.includes(draft.attribution_scope)) return new RevenuePersistenceError("validation", "validation: Select a valid attribution scope");
  const validStatuses = ["pending", "active", "disputed", "superseded", "rejected"];
  if (!validStatuses.includes(draft.status)) return new RevenuePersistenceError("validation", "validation: Select a valid attribution status");
  return { account_id: accountId, partner_id: partnerId, attribution_scope: draft.attribution_scope, scope_key: String(draft.entity_or_domain || "").trim() || null, effective_start: effectiveStart, effective_end: effectiveEnd, commission_start_date: String(draft.commission_start_date || "").trim() || effectiveStart, status: mapAttributionStatusToDb(draft.status), notes: String(draft.notes || "").trim() || null };
}

export async function createRevenueAttribution(draft: RevenueAttributionDraft): Promise<RevenueApiResult<RevenueAttribution>> {
  const payload = attributionPayload(draft);
  if (payload instanceof RevenuePersistenceError) return { data: null, error: payload };
  const result = await createRow("customer_attributions", payload);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Attribution was not created") };
  await writeRevenueAudit("attribution_created", "customer_attribution", text(result.data, "id"), null, result.data);
  return { data: { ...draft, id: text(result.data, "id"), partner_name: "Selected partner" }, error: null };
}

export async function updateRevenueAttribution(id: string, draft: RevenueAttributionDraft): Promise<RevenueApiResult<RevenueAttribution>> {
  const payload = attributionPayload(draft);
  if (payload instanceof RevenuePersistenceError) return { data: null, error: payload };
  const result = await updateRow("customer_attributions", id, payload);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Attribution was not updated") };
  await writeRevenueAudit("attribution_updated", "customer_attribution", id, null, result.data, "Customer attribution updated");
  return { data: { ...draft, id, partner_name: "Selected partner" }, error: null };
}

export async function endRevenueAttribution(id: string, effectiveEnd = new Date().toISOString().slice(0, 10)): Promise<RevenueApiResult<RevenueAttribution>> {
  const result = await updateRow("customer_attributions", id, { effective_end: effectiveEnd, status: "ended" });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Attribution was not ended") };
  await writeRevenueAudit("attribution_ended", "customer_attribution", id, null, result.data, "Customer attribution ended");
  return { data: mapAttribution(result.data, new Map(), new Map()), error: null };
}

export async function listRevenueProducts(): Promise<RevenueApiResult<RevenueProduct[]>> {
  const [products, prices] = await Promise.all([listRows("billing_products"), listRows("billing_prices")]);
  if (products.error) return { data: null, error: products.error };
  if (prices.error) return { data: null, error: prices.error };
  const priceByProduct = new Map((prices.data || []).map((price) => [text(price, "product_id"), price]));
  return { data: (products.data || []).map((row) => { const price = priceByProduct.get(text(row, "id")); return { id: text(row, "id"), product_name: text(row, "product_name"), product_key: text(row, "product_key"), revenue_category: mapProductCategory(text(row, "product_type")), commissionable_default: bool(row, "commissionable_default"), mrr_eligible_default: bool(row, "mrr_eligible_default", true), stripe_product_id: text(row, "stripe_product_id") || undefined, stripe_price_id: price ? text(price, "stripe_price_id") || undefined : undefined, billing_interval: (text(price || {}, "billing_interval", "month") as RevenueProduct["billing_interval"]), active: bool(row, "active", true) }; }), error: null };
}

export async function createRevenueProduct(draft: RevenueProductDraft): Promise<RevenueApiResult<RevenueProduct>> {
  const result = await createRow("billing_products", { product_key: draft.product_key.trim(), product_name: draft.product_name.trim(), product_type: draft.revenue_category === "other_software" ? "other" : draft.revenue_category, commissionable_default: draft.commissionable_default, mrr_eligible_default: draft.mrr_eligible_default, stripe_product_id: draft.stripe_product_id || null, active: draft.active });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Product was not created") };
  let priceError: Error | null = null;
  if (draft.stripe_price_id || draft.billing_interval) {
    const priceResult = await createRow("billing_prices", { product_id: text(result.data, "id"), stripe_price_id: draft.stripe_price_id || null, billing_interval: draft.billing_interval, active: draft.active });
    priceError = priceResult.error;
  }
  if (priceError) {
    await updateRow("billing_products", text(result.data, "id"), { active: false });
    return { data: null, error: new Error(`Product was archived because its price could not be saved: ${priceError.message}`) };
  }
  await writeRevenueAudit("product_created", "billing_product", text(result.data, "id"), null, result.data);
  return { data: { ...draft, id: text(result.data, "id") }, error: null };
}

export async function updateRevenueProductActive(id: string, active: boolean): Promise<RevenueApiResult<Row>> {
  const result = await updateRow("billing_products", id, { active });
  if (!result.error) await writeRevenueAudit("product_status_changed", "billing_product", id, null, { active });
  return result;
}

export async function updateRevenueProduct(id: string, draft: RevenueProduct): Promise<RevenueApiResult<RevenueProduct>> {
  const result = await updateRow("billing_products", id, { product_name: draft.product_name.trim(), product_type: draft.revenue_category === "other_software" ? "other" : draft.revenue_category, commissionable_default: draft.commissionable_default, mrr_eligible_default: draft.mrr_eligible_default, stripe_product_id: draft.stripe_product_id || null, active: draft.active });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Product was not updated") };
  const priceLookup = await supabase.from("billing_prices").select("id").eq("product_id", id).limit(1);
  if (priceLookup.error) return { data: null, error: toPersistenceError(priceLookup.error, "Could not read product price mapping") };
  const pricePayload = { stripe_price_id: draft.stripe_price_id || null, billing_interval: draft.billing_interval, active: draft.active };
  const priceResult = priceLookup.data?.[0]?.id
    ? await supabase.from("billing_prices").update(pricePayload).eq("id", priceLookup.data[0].id)
    : await supabase.from("billing_prices").insert({ ...pricePayload, product_id: id });
  if (priceResult.error) return { data: null, error: toPersistenceError(priceResult.error, "Could not save product price mapping") };
  await writeRevenueAudit("product_updated", "billing_product", id, null, result.data, "Billing product updated");
  return { data: draft, error: null };
}

export async function listRevenuePlans(): Promise<RevenueApiResult<RevenueCommissionPlan[]>> {
  const [plans, tiers] = await Promise.all([listRows("commission_plans"), listRows("commission_plan_tiers")]);
  if (plans.error) return { data: null, error: plans.error };
  if (tiers.error) return { data: null, error: tiers.error };
  return { data: (plans.data || []).map((row) => { const planTiers = (tiers.data || []).filter((tier) => text(tier, "plan_id") === text(row, "id")).sort((a, b) => Number(a.year_start) - Number(b.year_start)); return { id: text(row, "id"), plan_key: text(row, "plan_key"), plan_name: text(row, "plan_name"), plan_type: mapPlanType(text(row, "partner_type")), year_one_rate: number(planTiers[0] || {}, "commission_rate"), year_two_rate: number(planTiers[1] || {}, "commission_rate"), year_three_plus_rate: number(planTiers[2] || {}, "commission_rate"), payout_terms: mapPayoutTerms(text(row, "payout_terms")), requires_active_business: planTiers.some((tier) => bool(tier, "requires_active_business")), active: bool(row, "active", true), notes: text(row, "notes") || undefined, tiers: planTiers.map((tier) => ({ id: text(tier, "id"), year_start: Number(tier.year_start), year_end: tier.year_end === null || tier.year_end === undefined ? null : Number(tier.year_end), commission_rate: Number(tier.commission_rate), requires_active_business: bool(tier, "requires_active_business") })) }; }), error: null };
}

export async function createRevenuePlan(draft: RevenueCommissionPlanDraft): Promise<RevenueApiResult<RevenueCommissionPlan>> {
  const result = await createRow("commission_plans", { plan_key: draft.plan_key.trim(), plan_name: draft.plan_name.trim(), partner_type: mapPlanTypeToDb(draft.plan_type), payout_terms: draft.payout_terms, active: draft.active, notes: draft.notes || null });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Commission plan was not created") };
  if (draft.tiers?.length) {
    const { error } = await supabase.from("commission_plan_tiers").insert(draft.tiers.map((tier) => ({ ...tier, plan_id: text(result.data, "id") })));
    if (error) {
      await updateRow("commission_plans", text(result.data, "id"), { active: false });
      return { data: null, error: toPersistenceError(error, "Could not save commission plan tiers; the plan was archived") };
    }
  }
  await writeRevenueAudit("commission_plan_created", "commission_plan", text(result.data, "id"), null, result.data);
  return { data: { ...draft, id: text(result.data, "id") }, error: null };
}

export async function updateRevenuePlan(id: string, draft: RevenueCommissionPlanDraft): Promise<RevenueApiResult<RevenueCommissionPlan>> {
  if (!draft.plan_name.trim()) return { data: null, error: new RevenuePersistenceError("required_field", "required_field: Plan name is required") };
  const result = await updateRow("commission_plans", id, { plan_name: draft.plan_name.trim(), partner_type: mapPlanTypeToDb(draft.plan_type), payout_terms: draft.payout_terms, active: draft.active, notes: draft.notes || null });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Commission plan was not updated") };
  await writeRevenueAudit("commission_plan_updated", "commission_plan", id, null, result.data, "Commission plan updated");
  return { data: { ...draft, id, tiers: draft.tiers || [] }, error: null };
}

function validateTier(tier: { year_start: number; year_end?: number | null; commission_rate: number }) {
  if (!Number.isInteger(tier.year_start) || tier.year_start < 1) return "Year start must be an integer of at least 1";
  if (tier.year_end !== null && tier.year_end !== undefined && (!Number.isInteger(tier.year_end) || tier.year_end < tier.year_start)) return "Year end must be empty or at least the year start";
  if (tier.commission_rate < 0 || tier.commission_rate > 1) return "Commission rate must be between 0 and 1";
  return null;
}

export async function createRevenuePlanTier(planId: string, tier: Omit<RevenueCommissionPlanTier, "id">): Promise<RevenueApiResult<RevenueCommissionPlanTier>> {
  const validation = validateTier(tier);
  if (validation) return { data: null, error: new RevenuePersistenceError("validation", `validation: ${validation}`) };
  const result = await createRow("commission_plan_tiers", { ...tier, plan_id: planId });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Commission tier was not created") };
  await writeRevenueAudit("commission_tier_created", "commission_plan_tier", text(result.data, "id"), null, result.data, "Commission tier created");
  return { data: { id: text(result.data, "id"), ...tier }, error: null };
}

export async function updateRevenuePlanTier(id: string, tier: Omit<RevenueCommissionPlanTier, "id">): Promise<RevenueApiResult<RevenueCommissionPlanTier>> {
  const validation = validateTier(tier);
  if (validation) return { data: null, error: new RevenuePersistenceError("validation", `validation: ${validation}`) };
  const result = await updateRow("commission_plan_tiers", id, tier);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Commission tier was not updated") };
  await writeRevenueAudit("commission_tier_updated", "commission_plan_tier", id, null, result.data, "Commission tier updated");
  return { data: { id, ...tier }, error: null };
}

export async function removeRevenuePlanTier(id: string): Promise<RevenueApiResult<null>> {
  const { error } = await supabase.from("commission_plan_tiers").delete().eq("id", id);
  if (error) return { data: null, error: toPersistenceError(error, "Could not remove commission tier") };
  await writeRevenueAudit("commission_tier_removed", "commission_plan_tier", id, null, null, "Commission tier removed");
  return { data: null, error: null };
}

export async function listRevenueTasks(accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<RevenueAgentTask[]>> {
  const result = await listRows("agent_tasks");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => { const accountId = (row.related_account_id as string | null) || null; return { id: text(row, "id"), agent_key: text(row, "agent_key") as RevenueAgentTask["agent_key"], task_type: text(row, "task_type"), status: text(row, "status") as RevenueAgentTask["status"], priority: text(row, "priority") as RevenueAgentTask["priority"], related_account_id: accountId, related_account_name: accountId ? (accountDirectory.has(accountId) ? getAccountDisplayName(accountDirectory.get(accountId) as RevenueAccountProfile) : "Account mapping needs review") : null, related_partner_id: (row.related_partner_id as string | null) || null, related_opportunity_id: (row.related_opportunity_id as string | null) || null, related_invoice_id: (row.related_invoice_id as string | null) || null, related_commission_entry_id: (row.related_commission_entry_id as string | null) || null, title: text(row, "title"), summary: text(row, "summary") || null, payload_json: (row.payload_json as Record<string, unknown>) || {}, created_at: text(row, "created_at"), resolved_at: (row.resolved_at as string | null) || null, resolved_by: (row.resolved_by as string | null) || null }; }), error: null };
}

export async function listRevenueCommissionEntries(partnerById: Map<string, string>, planById: Map<string, string>, accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<RevenueCommissionEntry[]>> {
  const result = await listRows("commission_entries");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => { const accountId = text(row, "account_id"); const partnerId = text(row, "partner_id"); const account = accountDirectory.get(accountId); return { id: text(row, "id"), partner_id: partnerId || undefined, partner_name: partnerById.get(partnerId) || "Unknown partner", customer_account_id: accountId, customer_account: account ? getAccountDisplayName(account) : "Account mapping needs review", account_mapping_issue: Boolean(accountId && !account), eligible_revenue_event: text(row, "revenue_event_id") || undefined, revenue_amount: Number(text(row, "eligible_amount", "0")), commission_plan: planById.get(text(row, "plan_id")), commission_year: undefined, commission_rate: Number(text(row, "commission_rate", "0")), commission_amount: Number(text(row, "commission_amount", "0")), source_status: text(row, "status"), review_status: text(row, "review_status"), approved_at: text(row, "reviewed_at") || undefined, created_at: text(row, "created_at") || undefined, status: mapCommissionStatus(text(row, "status")), payable_date: text(row, "payable_at") || undefined, adjustment_indicator: false, notes: text(row, "notes") || undefined, entry_type: text(row, "entry_type") || undefined, metadata: row.metadata && typeof row.metadata === "object" ? row.metadata as Record<string, unknown> : undefined }; }), error: null };
}

export async function reviewCommissionEntry(entry: RevenueCommissionEntry, action: CommissionReviewAction, reviewNotes?: string | null): Promise<RevenueApiResult<Row>> {
  let update: Row;
  try {
    const { data: userData } = await supabase.auth.getUser();
    update = buildCommissionReviewUpdate(entry, action, userData.user?.id || null, new Date().toISOString(), reviewNotes);
  } catch (error) {
    return { data: null, error: new RevenuePersistenceError("validation", `validation: ${error instanceof Error ? error.message : "Commission entry cannot be reviewed"}`) };
  }
  const result = await updateRow("commission_entries", entry.id, update);
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Commission entry was not updated") };
  const auditAction = action === "approve" ? "commission_entry_approved" : action === "hold" ? "commission_entry_held" : action === "void" ? "commission_entry_voided" : "commission_entry_returned_to_review";
  await writeRevenueAudit(auditAction, "commission_entry", entry.id, { partner_id: entry.partner_id, account_id: entry.customer_account_id, revenue_event_id: entry.eligible_revenue_event, status: entry.source_status || entry.status }, { partner_id: entry.partner_id, account_id: entry.customer_account_id, revenue_event_id: entry.eligible_revenue_event, status: update.status }, reviewNotes || `Commission entry ${action.replaceAll("_", " ")}`);
  return result;
}

export async function listRevenuePayouts(): Promise<RevenueApiResult<RevenuePayoutRecord[]>> {
  const result = await listRows("commission_payout_batches");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => ({ id: text(row, "id"), partner_name: "Batch-level payout", amount: Number(text(row, "total_amount", "0")), status: mapPayoutStatus(text(row, "status")), approved_at: text(row, "approved_at") || undefined, paid_date: text(row, "paid_at") || undefined, payment_method: text(row, "payment_method") || undefined, payment_reference: text(row, "payment_reference") || undefined, payment_notes: text(row, "payment_notes") || undefined, created_at: text(row, "created_at") || undefined, notes: text(row, "notes") || undefined })), error: null };
}

export async function recordExternalPayoutPayment(batch: RevenuePayoutRecord, items: RevenuePayoutItem[], entries: RevenueCommissionEntry[], confirmation: PayoutPaymentConfirmation): Promise<RevenueApiResult<{ batch: RevenuePayoutRecord; items: RevenuePayoutItem[]; entries: RevenueCommissionEntry[] }>> {
  const validation = validatePayoutPaymentConfirmation(batch, items, entries, confirmation);
  if (validation) return { data: null, error: new RevenuePersistenceError("validation", `validation: ${validation}`) };
  const batchResult = await updateRow("commission_payout_batches", batch.id, { status: "paid", paid_at: confirmation.paid_at, notes: buildPayoutPaymentNotes(confirmation) });
  if (batchResult.error || !batchResult.data) return { data: null, error: batchResult.error || new Error("External payment confirmation could not be recorded") };
  const updatedItems: RevenuePayoutItem[] = [];
  for (const item of items) {
    const itemResult = await updateRow("commission_payout_items", item.id, { status: "paid" });
    if (itemResult.error || !itemResult.data) return { data: null, error: itemResult.error || new Error("Payout item payment status could not be updated") };
    updatedItems.push({ ...item, status: "paid" });
  }
  const entriesById = new Map(entries.map((entry) => [entry.id, entry]));
  const updatedEntries: RevenueCommissionEntry[] = [];
  for (const item of items) {
    const entry = entriesById.get(item.commission_entry_id);
    if (!entry) continue;
    const entryResult = await updateRow("commission_entries", entry.id, { status: "paid" });
    if (entryResult.error || !entryResult.data) return { data: null, error: entryResult.error || new Error("Commission entry payment status could not be updated") };
    updatedEntries.push({ ...entry, status: "paid", source_status: "paid" });
  }
  await writeRevenueAudit("payout_batch_payment_recorded", "commission_payout_batch", batch.id, { status: batch.status }, buildPayoutPaymentAuditMetadata(batch, items, confirmation), "External payout payment recorded; no payment was initiated by Toren");
  return { data: { batch: { ...batch, status: "paid", paid_date: confirmation.paid_at, notes: buildPayoutPaymentNotes(confirmation) }, items: updatedItems, entries: updatedEntries }, error: null };
}

export async function listRevenuePayoutItems(): Promise<RevenueApiResult<RevenuePayoutItem[]>> {
  const result = await listRows("commission_payout_items");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => ({ id: text(row, "id"), payout_batch_id: text(row, "payout_batch_id"), partner_id: text(row, "partner_id"), commission_entry_id: text(row, "commission_entry_id"), amount: Number(text(row, "amount", "0")), status: text(row, "status", "included") as RevenuePayoutItem["status"] })), error: null };
}

export type RevenuePayoutPreparationResult = {
  batch: RevenuePayoutRecord;
  items: RevenuePayoutItem[];
};

export async function createManualPayoutBatch(entries: PayoutPreparationEntry[], selectedIds: string[]): Promise<RevenueApiResult<RevenuePayoutPreparationResult>> {
  const validation = validatePayoutSelection(entries, selectedIds);
  if (validation) return { data: null, error: new RevenuePersistenceError("validation", `validation: ${validation}`) };
  const selected = entries.filter((entry) => selectedIds.includes(entry.id));
  const existing = await supabase.from("commission_payout_items").select("commission_entry_id").in("commission_entry_id", selectedIds);
  if (existing.error) return { data: null, error: toPersistenceError(existing.error, "Could not check payout item duplicates") };
  if ((existing.data || []).length) return { data: null, error: new RevenuePersistenceError("conflict", "conflict: One or more selected commissions are already in a payout batch.") };
  const batchDraft = buildPayoutBatchDraft(selected);
  const batchResult = await createRow("commission_payout_batches", { payout_period_start: batchDraft.payout_period_start, payout_period_end: batchDraft.payout_period_end, status: "draft", total_amount: batchDraft.amount, approved_by: null, approved_at: null, paid_at: null, notes: "Prepared for manual reconciliation; no external payment created." });
  if (batchResult.error || !batchResult.data) return { data: null, error: batchResult.error || new Error("Payout batch was not created") };
  const batchId = text(batchResult.data, "id");
  const itemPayload = selected.map((entry) => ({ payout_batch_id: batchId, partner_id: entry.partner_id, commission_entry_id: entry.id, amount: entry.commission_amount, status: "included" }));
  const { data: itemRows, error: itemError } = await supabase.from("commission_payout_items").insert(itemPayload).select("*");
  if (itemError || !itemRows) {
    await updateRow("commission_payout_batches", batchId, { status: "void", notes: "Preparation failed; no payout was executed." });
    return { data: null, error: toPersistenceError(itemError, "Could not create payout items") };
  }
  const items = (itemRows as Row[]).map((row) => ({ id: text(row, "id"), payout_batch_id: text(row, "payout_batch_id"), partner_id: text(row, "partner_id"), commission_entry_id: text(row, "commission_entry_id"), amount: Number(text(row, "amount", "0")), status: text(row, "status", "included") as RevenuePayoutItem["status"] }));
  const batch: RevenuePayoutRecord = { id: batchId, partner_name: batchDraft.partner_count > 1 ? "Multiple partners" : "Manual payout preparation", amount: batchDraft.amount, status: "draft", item_count: items.length, partner_count: batchDraft.partner_count, created_at: text(batchResult.data, "created_at") || undefined, notes: text(batchResult.data, "notes") || undefined };
  await writeRevenueAudit("payout_batch_prepared", "commission_payout_batch", batchId, null, { commission_entry_ids: selectedIds, total_amount: batch.amount, item_count: items.length, partner_ids: selected.map((entry) => entry.partner_id), status: "draft" }, "Payout batch prepared for manual review");
  for (const item of items) await writeRevenueAudit("payout_item_created", "commission_payout_item", item.id, null, item, "Payout item created for manual reconciliation");
  return { data: { batch, items }, error: null };
}

export async function updateRevenuePayoutStatus(id: string, status: RevenuePayoutRecord["status"]): Promise<RevenueApiResult<Row>> {
  const result = await updateRow("commission_payout_batches", id, { status: mapPayoutStatusToDb(status) });
  if (!result.error) await writeRevenueAudit("payout_status_changed", "commission_payout_batch", id, null, { status });
  return result;
}

export async function createRevenueTask(task: RevenueAgentTask): Promise<RevenueApiResult<RevenueAgentTask>> {
  const result = await createRow("agent_tasks", { agent_key: task.agent_key, task_type: task.task_type, status: task.status, priority: task.priority, related_account_id: task.related_account_id || null, related_partner_id: task.related_partner_id || null, related_opportunity_id: task.related_opportunity_id || null, related_invoice_id: task.related_invoice_id || null, related_commission_entry_id: task.related_commission_entry_id || null, title: task.title.trim(), summary: task.summary || null, payload_json: task.payload_json || {} });
  if (result.error || !result.data) return { data: null, error: result.error || new Error("Agent task was not created") };
  await writeRevenueAudit("agent_task_created", "agent_task", text(result.data, "id"), null, result.data);
  return { data: { ...task, id: text(result.data, "id"), created_at: text(result.data, "created_at") }, error: null };
}

export async function updateRevenueTaskStatus(taskId: string, status: RevenueAgentTask["status"], summary?: string | null): Promise<RevenueApiResult<RevenueAgentTask>> {
  const { data, error } = await supabase.from("agent_tasks").update({ status, summary: summary || null, resolved_at: ["completed", "dismissed"].includes(status) ? new Date().toISOString() : null }).eq("id", taskId).select("*").single();
  if (error) return { data: null, error: toPersistenceError(error, "Could not update agent task") };
  if (!data) return { data: null, error: new Error("Agent task was not updated") };
  await writeRevenueAudit("agent_task_status_changed", "agent_task", String(data.id), null, data);
  return { data: data as RevenueAgentTask, error: null };
}

export async function listRevenueBillingRecords(accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<RevenueBillingRecord[]>> {
  const [customers, subscriptions, invoices, payments] = await Promise.all([
    listRows("billing_customers"),
    listRows("billing_subscriptions"),
    listRows("billing_invoices"),
    listRows("billing_payments"),
  ]);
  const firstError = [customers, subscriptions, invoices, payments].find((result) => result.error)?.error;
  if (firstError) return { data: null, error: firstError };
  const subscriptionByCustomer = new Map((subscriptions.data || []).map((row) => [text(row, "billing_customer_id"), row]));
  const invoiceByAccount = new Map((invoices.data || []).map((row) => [text(row, "account_id"), row]));
  const paymentByInvoice = new Map((payments.data || []).map((row) => [text(row, "invoice_id"), row]));
  return {
    data: (customers.data || []).map((row) => {
      const subscription = subscriptionByCustomer.get(text(row, "id"));
      const invoice = invoiceByAccount.get(text(row, "account_id"));
      const payment = invoice ? paymentByInvoice.get(text(invoice, "id")) : undefined;
      return {
        id: text(row, "id"),
        account_id: text(row, "account_id"),
        account_name: accountDirectory.has(text(row, "account_id")) ? getAccountDisplayName(accountDirectory.get(text(row, "account_id")) as RevenueAccountProfile) : "Account mapping needs review",
        account_domain: getAccountDomain(accountDirectory.get(text(row, "account_id")) || { account_id: text(row, "account_id") }),
        account_mapping_issue: !accountDirectory.has(text(row, "account_id")),
        stripe_customer_id: text(row, "stripe_customer_id") || undefined,
        stripe_subscription_id: text(subscription || {}, "stripe_subscription_id") || undefined,
        stripe_invoice_id: text(invoice || {}, "stripe_invoice_id") || undefined,
        billing_status: text(row, "status"),
        subscription_status: text(subscription || {}, "status") || undefined,
        invoice_status: text(invoice || {}, "status") || undefined,
        payment_status: text(payment || {}, "status") || undefined,
        last_payment_date: text(invoice || {}, "paid_at") || undefined,
        failed_payment: text(payment || {}, "status") === "failed",
        notes: subscription || invoice ? undefined : "Subscription and invoice details will appear after billing sync.",
      };
    }),
    error: null,
  };
}

export async function backfillStripeInvoiceLineMappings(): Promise<RevenueApiResult<{ updated: number; skipped: number }>> {
  const [lines, invoices, events, products, prices] = await Promise.all([
    listRows("billing_invoice_lines"),
    listRows("billing_invoices"),
    listRows("stripe_webhook_events"),
    listRows("billing_products"),
    listRows("billing_prices"),
  ]);
  const firstError = [lines, invoices, events, products, prices].find((result) => result.error)?.error;
  if (firstError) return { data: null, error: firstError };

  const invoiceById = new Map((invoices.data || []).map((row) => [text(row, "id"), row]));
  const eventByInvoice = new Map<string, Row>();
  for (const row of events.data || []) {
    if (!/^invoice\.(paid|payment_succeeded|finalized)$/.test(text(row, "event_type"))) continue;
    const payload = (row.payload_json as Row) || {};
    const stripeInvoiceId = text(((payload.data as Row) || {}).object as Row || {}, "id");
    if (stripeInvoiceId && !eventByInvoice.has(stripeInvoiceId)) eventByInvoice.set(stripeInvoiceId, payload);
  }

  let updated = 0;
  let skipped = 0;
  for (const row of lines.data || []) {
    const invoice = invoiceById.get(text(row, "invoice_id"));
    const payload = invoice ? eventByInvoice.get(text(invoice, "stripe_invoice_id")) : undefined;
    const stripeLineId = text(row, "stripe_invoice_line_id");
    const stripeLine = extractStripeInvoiceLineFromPayload(payload as Record<string, any> | undefined, stripeLineId || null);
    if (!stripeLine) {
      skipped += 1;
      continue;
    }
    const currentMetadata = (row.metadata as Row) || {};
    const mapping = mapStripeInvoiceLine(stripeLine, { products: products.data || [], prices: prices.data || [], metadata: currentMetadata });
    const shouldUpdate = !currentMetadata.stripe_product_id || !currentMetadata.stripe_price_id || !row.product_id || !row.price_id || row.commissionability_reason === "Unmatched product or price; manual review required";
    if (!shouldUpdate) {
      skipped += 1;
      continue;
    }
    const result = await supabase.from("billing_invoice_lines").update({
      product_id: mapping.product_id,
      price_id: mapping.price_id,
      commissionable: mapping.commissionable,
      commissionability_reason: mapping.commissionability_reason,
      metadata: mapping.metadata,
    }).eq("id", text(row, "id")).select("*").single();
    if (result.error) return { data: null, error: toPersistenceError(result.error, "Could not backfill Stripe invoice line mappings") };
    updated += 1;
  }
  return { data: { updated, skipped }, error: null };
}

export async function loadEligibleRevenueReview(accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<EligibleRevenueReviewData>> {
  const [invoices, payments, adjustments, customers, attributions, assignments, partners, lines, products, prices, stagedEvents, commissionPlans] = await Promise.all([
    listRows("billing_invoices"),
    listRows("billing_payments"),
    listRows("billing_adjustments"),
    listRows("billing_customers"),
    listRows("customer_attributions"),
    listRows("partner_plan_assignments"),
    listRows("sales_partners"),
    listRows("billing_invoice_lines"),
    listRows("billing_products"),
    listRows("billing_prices"),
    listRows("eligible_revenue_events"),
    listRows("commission_plans"),
  ]);
  if (invoices.error) return { data: null, error: invoices.error };
  // Supporting tables are deliberately optional: a missing attribution, plan,
  // product mapping, or staging table must become candidate blockers rather
  // than making paid invoice candidates disappear from the review surface.
  const planNames = new Map((commissionPlans.data || []).map((row) => [text(row, "id"), text(row, "plan_name")]));
  const planRows = (assignments.data || []).map((row) => ({ ...row, plan_name: text(row, "plan_name") || planNames.get(text(row, "plan_id")) || "" })) as EligibleRevenueRow[];
  return {
    data: deriveEligibleRevenueReviewData({
      invoices: invoices.data || [],
      payments: payments.data || [],
      adjustments: adjustments.data || [],
      accounts: Array.from(accountDirectory.values()) as unknown as EligibleRevenueRow[],
      customers: customers.data || [],
      attributions: attributions.data || [],
      assignments: planRows,
      partners: partners.data || [],
      lines: lines.data || [],
      products: products.data || [],
      prices: prices.data || [],
      stagedEvents: stagedEvents.data || [],
    }),
    error: null,
  };
}

export async function loadCommissionReadinessReview(accountDirectory: RevenueAccountDirectory = new Map()): Promise<RevenueApiResult<CommissionReadinessReviewData>> {
  const [events, invoices, payments, adjustments, attributions, partners, assignments, plans, tiers, relationships, commissionEntries] = await Promise.all([
    listRows("eligible_revenue_events"),
    listRows("billing_invoices"),
    listRows("billing_payments"),
    listRows("billing_adjustments"),
    listRows("customer_attributions"),
    listRows("sales_partners"),
    listRows("partner_plan_assignments"),
    listRows("commission_plans"),
    listRows("commission_plan_tiers"),
    listRows("sales_partner_relationships"),
    listRows("commission_entries"),
  ]);
  if (events.error) return { data: null, error: events.error };
  // Supporting tables are optional for the preflight. Missing context is more
  // useful as a blocked/review-needed row than as an empty commission screen.
  return {
    data: deriveCommissionReadinessReviewData({
      eligibleRevenueEvents: events.data || [],
      invoices: invoices.data || [],
      payments: payments.data || [],
      adjustments: adjustments.data || [],
      accounts: Array.from(accountDirectory.values()) as unknown as CommissionReadinessRow[],
      attributions: attributions.data || [],
      partners: partners.data || [],
      partnerPlanAssignments: assignments.data || [],
      commissionPlans: plans.data || [],
      commissionPlanTiers: tiers.data || [],
      partnerRelationships: relationships.data || [],
      commissionEntries: commissionEntries.data || [],
    }),
    error: null,
  };
}

export function buildEligibleRevenueEventPayload(candidate: EligibleRevenueCandidate, stagedBy?: string | null): Row {
  return {
    account_id: candidate.account_id,
    invoice_id: candidate.invoice_id || null,
    payment_id: candidate.payment_id || null,
    attribution_id: candidate.attribution_id || null,
    revenue_type: "software_subscription",
    gross_amount: candidate.amount_paid,
    excluded_amount: candidate.amount_refunded,
    eligible_amount: candidate.retained_amount,
    currency: candidate.currency || "usd",
    received_at: candidate.paid_at || candidate.invoice_date || new Date().toISOString(),
    retention_status: "pending_review",
    source_type: "stripe_invoice_payment",
    source_invoice_id: candidate.invoice_id || null,
    source_payment_id: candidate.payment_id || null,
    source_key: candidate.review_key,
    review_status: "pending_review",
    staged_at: new Date().toISOString(),
    staged_by: stagedBy || null,
    metadata: {
      invoice_number: candidate.invoice_number || null,
      stripe_invoice_id: candidate.stripe_invoice_id || null,
      amount_paid: candidate.amount_paid,
      amount_refunded: candidate.amount_refunded,
      retained_amount: candidate.retained_amount,
      candidate_status: candidate.status,
      reasons: candidate.reasons,
      blocker_codes: candidate.blocker_codes,
      manual_stage: true,
    },
  };
}

function mapEligibleRevenueEvent(row: Row): EligibleRevenueEvent {
  return {
    id: text(row, "id"),
    account_id: text(row, "account_id"),
    invoice_id: text(row, "invoice_id") || undefined,
    payment_id: text(row, "payment_id") || undefined,
    attribution_id: text(row, "attribution_id") || undefined,
    source_type: text(row, "source_type", "stripe_invoice_payment"),
    source_invoice_id: text(row, "source_invoice_id") || undefined,
    source_payment_id: text(row, "source_payment_id") || undefined,
    source_key: text(row, "source_key"),
    review_status: text(row, "review_status", "pending_review"),
    staged_at: text(row, "staged_at") || text(row, "created_at"),
    staged_by: text(row, "staged_by") || undefined,
    metadata: (row.metadata as Row) || {},
  };
}

export async function stageEligibleRevenueCandidate(candidate: EligibleRevenueCandidate): Promise<RevenueApiResult<EligibleRevenueEvent> & { reused?: boolean }> {
  if (candidate.status !== "ready" || candidate.retained_amount <= 0 || !candidate.account_id || !candidate.attribution_id || !candidate.plan_id) {
    return { data: null, error: new RevenuePersistenceError("validation", "validation: Candidate is not ready for eligibility review staging") };
  }
  const existingResult = await supabase.from("eligible_revenue_events").select("*").eq("source_key", candidate.review_key).limit(1);
  if (existingResult.error) return { data: null, error: toPersistenceError(existingResult.error, "Could not check existing eligible revenue review records") };
  if (existingResult.data?.[0]) return { data: mapEligibleRevenueEvent(existingResult.data[0] as Row), error: null, reused: true };
  const { data: userData } = await supabase.auth.getUser();
  const payload = buildEligibleRevenueEventPayload(candidate, userData.user?.id || null);
  const inserted = await supabase.from("eligible_revenue_events").insert(payload).select("*").single();
  if (inserted.error) {
    if (inserted.error.code === "23505") {
      const retry = await supabase.from("eligible_revenue_events").select("*").eq("source_key", candidate.review_key).limit(1);
      if (retry.data?.[0]) return { data: mapEligibleRevenueEvent(retry.data[0] as Row), error: null, reused: true };
    }
    return { data: null, error: toPersistenceError(inserted.error, "Could not stage eligible revenue review record") };
  }
  const event = mapEligibleRevenueEvent((inserted.data || {}) as Row);
  await writeRevenueAudit("eligible_revenue_event_staged", "eligible_revenue_event", event.id, null, event, "Manual eligible revenue review staging");
  return { data: event, error: null, reused: false };
}

export async function writeRevenueAudit(action: string, entityType: string, entityId: string, beforeJson: unknown, afterJson: unknown, reason?: string) {
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await supabase.from("revenue_audit_log").insert({ actor_user_id: userData.user?.id || null, action, entity_type: entityType, entity_id: entityId, before_json: beforeJson, after_json: afterJson, reason: reason || null });
  if (error) console.warn("Revenue audit entry could not be written:", error.message);
}

export async function listRevenueAuditLog(): Promise<RevenueApiResult<RevenueAuditLogEntry[]>> {
  const result = await listRows("revenue_audit_log");
  if (result.error) return { data: null, error: result.error };
  return { data: (result.data || []).map((row) => ({ id: text(row, "id"), action: text(row, "action"), entity_type: text(row, "entity_type"), entity_id: text(row, "entity_id") || undefined, actor_user_id: text(row, "actor_user_id") || undefined, created_at: text(row, "created_at"), reason: text(row, "reason") || undefined })), error: null };
}

export async function listRevenueWebhookEvents(): Promise<RevenueApiResult<RevenueStripeWebhookEvent[]>> {
  const { data, error } = await supabase.from("stripe_webhook_events").select("*").order("created_at", { ascending: false }).limit(100);
  if (error) return { data: null, error: toPersistenceError(error, "Could not read Stripe webhook events") };
  return { data: (data || []).map(mapWebhookEvent), error: null };
}

function mapWebhookEvent(row: Row): RevenueStripeWebhookEvent {
  const payload = (row.payload_json as Row) || {};
  const object = ((payload.data as Row) || {}).object as Row || {};
  return {
    id: text(row, "id"),
    stripe_event_id: text(row, "stripe_event_id"),
    event_type: text(row, "event_type"),
    status: text(row, "status") as RevenueStripeWebhookEvent["status"],
    created_at: text(row, "created_at") || undefined,
    received_at: text(row, "received_at"),
    processed_at: text(row, "processed_at") || undefined,
    related_stripe_customer_id: text(row, "related_stripe_customer_id") || undefined,
    related_stripe_invoice_id: text(row, "related_stripe_invoice_id") || undefined,
    related_stripe_subscription_id: text(row, "related_stripe_subscription_id") || undefined,
    invoice_number: text(object, "number") || undefined,
    error_message: text(row, "error_message") || undefined,
    livemode: typeof object.livemode === "boolean" ? object.livemode : undefined,
  };
}

function mirrorSample(row: Row, detail?: string): RevenueMirrorIntegrityIssueSample {
  const metadata = (row.metadata as Row) || {};
  return {
    id: text(row, "id") || undefined,
    account_id: text(row, "account_id") || undefined,
    invoice_id: text(row, "invoice_id") || undefined,
    payment_id: text(row, "payment_id") || undefined,
    stripe_invoice_id: text(row, "stripe_invoice_id") || text(metadata, "stripe_invoice_id") || undefined,
    stripe_payment_intent_id: text(row, "stripe_payment_intent_id") || text(metadata, "stripe_payment_intent_id") || undefined,
    livemode: typeof row.livemode === "boolean" ? row.livemode : undefined,
    stripe_event_id: text(row, "stripe_event_id") || undefined,
    event_type: text(row, "event_type") || undefined,
    detail,
  };
}

function distinctRefundKey(row: Row) {
  const metadata = (row.metadata as Row) || {};
  return text(metadata, "canonical_refund_key") || text(metadata, "stripe_refund_id") || text(row, "stripe_object_id") || `${text(row, "id")}:${text(row, "amount")}`;
}

export function deriveStripeMirrorIntegrityChecks({ invoices = [], payments = [], adjustments = [], events = [] }: { invoices?: Row[]; payments?: Row[]; adjustments?: Row[]; events?: RevenueStripeWebhookEvent[] }): RevenueMirrorIntegrityCheck[] {
  const paymentInvoiceIds = new Set(payments.map((row) => text(row, "invoice_id")));
  const refundAdjustments = adjustments.filter((row) => text(row, "adjustment_type") === "refund");
  const paidMissingPayment = invoices.filter((row) => text(row, "status") === "paid" && Number(row.amount_paid || 0) > 0 && !paymentInvoiceIds.has(text(row, "id")));
  const refundedPaymentIds = new Set(refundAdjustments.map((row) => text(row, "payment_id")));
  const refundedMissingAdjustment = payments.filter((row) => ["refunded", "partially_refunded"].includes(text(row, "status")) && !refundedPaymentIds.has(text(row, "id")));
  const unlinkedRefunds = refundAdjustments.filter((row) => !text(row, "invoice_id") || !text(row, "payment_id"));
  const invoiceMismatch = invoices.filter((invoice) => {
    const linked = refundAdjustments.filter((row) => text(row, "invoice_id") === text(invoice, "id"));
    const seen = new Set<string>();
    const distinctAmount = linked.reduce((sum, row) => {
      const key = distinctRefundKey(row);
      if (seen.has(key)) return sum;
      seen.add(key);
      return sum + Math.abs(Number(row.amount || 0));
    }, 0);
    const cap = Number(invoice.total_amount || invoice.amount_paid || 0);
    const expected = cap > 0 ? Math.min(distinctAmount, cap) : distinctAmount;
    return Math.abs(Number(invoice.amount_refunded || 0) - expected) > 0.005;
  });
  const canonicalGroups = new Map<string, Row[]>();
  refundAdjustments.forEach((row) => {
    const metadata = (row.metadata as Row) || {};
    const key = text(metadata, "canonical_refund_key") || text(metadata, "stripe_refund_id");
    if (key) canonicalGroups.set(key, [...(canonicalGroups.get(key) || []), row]);
  });
  const duplicateCanonical = [...canonicalGroups.entries()].filter(([, rows]) => rows.length > 1);
  const failedEvents = events.filter((event) => event.status === "failed");
  const integrityReviewKey = (key: string, sample: RevenueMirrorIntegrityIssueSample) => `stripe_integrity:${key}${sample.invoice_id ? `:${sample.invoice_id}` : sample.payment_id ? `:${sample.payment_id}` : sample.id ? `:${sample.id}` : ""}`;
  const check = (key: string, label: string, count: number, severity: RevenueMirrorIntegrityCheck["severity"], explanation: string, samples: RevenueMirrorIntegrityIssueSample[]): RevenueMirrorIntegrityCheck => ({ key, label, count, severity, explanation, samples: samples.slice(0, 5).map((sample) => ({ ...sample, review_key: sample.review_key || integrityReviewKey(key, sample) })) });
  return [
    check("paid_invoice_missing_payment", "Paid invoices missing payment", paidMissingPayment.length, paidMissingPayment.length ? "high" : "info", "Paid invoices with collected amounts should have one mirrored payment row.", paidMissingPayment.map((row) => mirrorSample(row, "Paid invoice has no billing payment"))),
    check("refunded_payment_missing_adjustment", "Refunded payments missing adjustment", refundedMissingAdjustment.length, refundedMissingAdjustment.length ? "high" : "info", "Refunded or partially refunded payments should have a linked refund adjustment.", refundedMissingAdjustment.map((row) => mirrorSample(row, "Payment status indicates a refund but no adjustment is linked"))),
    check("unlinked_refund_adjustment", "Refund adjustments not linked", unlinkedRefunds.length, unlinkedRefunds.length ? "warning" : "info", "Refund adjustments should link to both the mirrored invoice and payment when available.", unlinkedRefunds.map((row) => mirrorSample(row, "Refund adjustment is missing invoice or payment linkage"))),
    check("invoice_refund_mismatch", "Refund total mismatch", invoiceMismatch.length, invoiceMismatch.length ? "high" : "info", "Invoice amount_refunded should match distinct linked refund adjustments, capped at the invoice amount.", invoiceMismatch.map((row) => mirrorSample(row, "Invoice refund total differs from linked refund adjustments"))),
    check("duplicate_canonical_refund", "Duplicate refund mirror rows", duplicateCanonical.reduce((sum, [, rows]) => sum + rows.length - 1, 0), duplicateCanonical.length ? "high" : "info", "Each canonical refund identity should have one adjustment row.", duplicateCanonical.flatMap(([key, rows]) => rows.map((row) => mirrorSample(row, `Duplicate canonical refund identity: ${key}`)))),
    check("failed_webhook", "Failed webhook events", failedEvents.length, failedEvents.length ? "high" : "info", "Failed webhook events need operator or TJ review before billing state is trusted.", failedEvents.map((event) => ({ id: event.id, stripe_event_id: event.stripe_event_id, event_type: event.event_type, livemode: event.livemode, review_key: `stripe_failed_event:${event.stripe_event_id}`, detail: event.error_message || "Webhook processing failed" }))),
  ];
}

export async function loadRevenueStripeMirrorHealth(): Promise<RevenueApiResult<{ events: RevenueStripeWebhookEvent[]; health: RevenueStripeMirrorHealth }>> {
  const [eventResult, invoiceResult, paymentResult, adjustmentResult] = await Promise.all([
    listRevenueWebhookEvents(),
    listRows("billing_invoices"),
    listRows("billing_payments"),
    listRows("billing_adjustments"),
  ]);
  const firstError = [eventResult, invoiceResult, paymentResult, adjustmentResult].find((result) => result.error)?.error;
  if (firstError) return { data: null, error: firstError };
  const events = eventResult.data || [];
  const checks = deriveStripeMirrorIntegrityChecks({ invoices: invoiceResult.data || [], payments: paymentResult.data || [], adjustments: adjustmentResult.data || [], events });
  const failed = events.filter((event) => event.status === "failed").length;
  const reviewCount = failed + checks.filter((item) => item.count > 0 && item.key !== "failed_webhook").reduce((sum, item) => sum + item.count, 0);
  return {
    data: {
      events,
      health: {
        last_event_received_at: events[0]?.received_at || events[0]?.created_at,
        last_event_processed_at: events.filter((event) => event.processed_at).sort((a, b) => String(b.processed_at).localeCompare(String(a.processed_at)))[0]?.processed_at,
        recent_processed_count: events.filter((event) => event.status === "processed").length,
        recent_ignored_count: events.filter((event) => event.status === "ignored").length,
        recent_failed_count: failed,
        recent_review_worthy_count: reviewCount,
        integrity_checks: checks,
      },
    },
    error: null,
  };
}

export async function createStripeMirrorReviewTask(input: CreateStripeMirrorReviewTaskInput): Promise<RevenueApiResult<RevenueAgentTask> & { reused?: boolean }> {
  let openQuery = supabase.from("agent_tasks").select("*").eq("agent_key", "tj_revenue_ops").eq("task_type", input.task_type);
  const eventId = input.payload_json?.stripe_event_id;
  if (input.related_invoice_id) openQuery = openQuery.eq("related_invoice_id", input.related_invoice_id);
  const open = await openQuery.limit(100);
  if (open.error) return { data: null, error: toPersistenceError(open.error, "Could not check existing TJ review tasks") };
  const reviewKey = input.review_key || input.payload_json?.review_key;
  const existing = (open.data || []).find((row) => {
    const payload = (row.payload_json as Row) || {};
    if (reviewKey) return text(payload, "review_key") === String(reviewKey);
    return eventId ? text(payload, "stripe_event_id") === String(eventId) : true;
  });
  if (existing) return { data: existing as RevenueAgentTask, error: null, reused: true };
  return createRevenueTask({ agent_key: "tj_revenue_ops", task_type: input.task_type, status: "needs_review", priority: "normal", related_account_id: input.related_account_id || null, related_invoice_id: input.related_invoice_id || null, title: input.title, summary: input.summary, payload_json: { ...(input.payload_json || {}), ...(reviewKey ? { review_key: reviewKey } : {}) } });
}

export async function acknowledgeStripeMirrorReviewTask(input: CreateStripeMirrorReviewTaskInput): Promise<RevenueApiResult<RevenueAgentTask> & { reused?: boolean }> {
  const reviewKey = input.review_key || input.payload_json?.review_key;
  const existingRows = await supabase.from("agent_tasks").select("*").eq("agent_key", "tj_revenue_ops").limit(100);
  if (existingRows.error) return { data: null, error: toPersistenceError(existingRows.error, "Could not check existing Stripe health acknowledgement") };
  const existing = (existingRows.data || []).find((row) => String(((row.payload_json as Row) || {}).review_key || "") === String(reviewKey || "")) as RevenueAgentTask | undefined;
  const result = existing ? { data: existing, error: null, reused: true } : await createStripeMirrorReviewTask(input);
  if (result.error || !result.data || !result.data.id) return result;
  if (!["completed", "dismissed"].includes(result.data.status)) {
    const dismissed = await updateRevenueTaskStatus(result.data.id, "dismissed", "Acknowledged by internal admin. No Stripe, billing, commission, or payout state was changed.");
    if (dismissed.error || !dismissed.data) return { data: null, error: dismissed.error || new Error("Notification acknowledgement could not be saved"), reused: result.reused };
    return { data: dismissed.data, error: null, reused: result.reused };
  }
  return result;
}

export async function createEligibleRevenueReviewTask(input: CreateEligibleRevenueReviewTaskInput): Promise<RevenueApiResult<RevenueAgentTask> & { reused?: boolean }> {
  const reviewKey = input.payload_json.review_key;
  const existingTasks = await supabase.from("agent_tasks").select("*").eq("agent_key", "tj_revenue_ops").eq("task_type", "eligible_revenue_review").limit(100);
  if (existingTasks.error) return { data: null, error: toPersistenceError(existingTasks.error, "Could not check existing eligible revenue review tasks") };
  const existing = (existingTasks.data || []).find((row) => String(((row.payload_json as Row) || {}).review_key || "") === String(reviewKey));
  if (existing) return { data: existing as RevenueAgentTask, error: null, reused: true };
  return createRevenueTask({
    agent_key: "tj_revenue_ops",
    task_type: "eligible_revenue_review",
    status: "needs_review",
    priority: input.payload_json.blocker_codes && Array.isArray(input.payload_json.blocker_codes) && input.payload_json.blocker_codes.length ? "high" : "normal",
    related_account_id: input.related_account_id || null,
    related_invoice_id: input.related_invoice_id || null,
    title: input.title,
    summary: input.summary,
    payload_json: input.payload_json,
  });
}

export async function createCommissionReadinessReviewTask(input: CreateCommissionReadinessReviewTaskInput): Promise<RevenueApiResult<RevenueAgentTask> & { reused?: boolean }> {
  const reviewKey = input.payload_json.review_key;
  const open = await supabase.from("agent_tasks").select("*").eq("agent_key", "tj_revenue_ops").eq("task_type", "commission_readiness_review").in("status", ["queued", "in_progress", "needs_review"]).limit(100);
  if (open.error) return { data: null, error: toPersistenceError(open.error, "Could not check existing commission readiness review tasks") };
  const existing = (open.data || []).find((row) => String(((row.payload_json as Row) || {}).review_key || "") === String(reviewKey));
  if (existing) return { data: existing as RevenueAgentTask, error: null, reused: true };
  return createRevenueTask({
    agent_key: "tj_revenue_ops",
    task_type: "commission_readiness_review",
    status: "needs_review",
    priority: input.priority || "normal",
    related_account_id: input.related_account_id || null,
    related_invoice_id: input.related_invoice_id || null,
    title: input.title,
    summary: input.summary,
    payload_json: input.payload_json,
  });
}

export async function createDraftCommissionEntry(candidate: CommissionReadinessCandidate): Promise<RevenueApiResult<Row> & { reused?: boolean }> {
  const eventId = candidate.eligible_revenue_event_id;
  const rate = Number(candidate.draft_rate);
  const commissionAmount = Number(candidate.draft_commission_amount);
  if (!canCreateDraftCommissionEntry(candidate)) {
    return { data: null, error: new RevenuePersistenceError("validation", "validation: Only a ready preflight row with complete partner, plan, amount, and rate context can create a draft commission entry") };
  }
  const planId = candidate.commission_plan_id;
  const sourceKey = buildCommissionEntrySourceKey(eventId);
  const existingBySource = await supabase.from("commission_entries").select("*").eq("source_key", sourceKey).limit(1);
  if (existingBySource.error) return { data: null, error: toPersistenceError(existingBySource.error, "Could not check existing draft commission entries") };
  if (existingBySource.data?.[0]) {
    await writeRevenueAudit("commission_draft_reused", "commission_entry", text(existingBySource.data[0] as Row, "id"), null, existingBySource.data[0], "Existing draft commission entry reused");
    return { data: existingBySource.data[0] as Row, error: null, reused: true };
  }
  const existingByEvent = await supabase.from("commission_entries").select("*").eq("revenue_event_id", eventId).eq("partner_id", candidate.partner_id).limit(1);
  if (existingByEvent.error) return { data: null, error: toPersistenceError(existingByEvent.error, "Could not check existing event commission entries") };
  if (existingByEvent.data?.[0]) {
    await writeRevenueAudit("commission_draft_reused", "commission_entry", text(existingByEvent.data[0] as Row, "id"), null, existingByEvent.data[0], "Existing event commission entry reused");
    return { data: existingByEvent.data[0] as Row, error: null, reused: true };
  }
  const { data: userData } = await supabase.auth.getUser();
  const metadata: Row = {
    manual_draft: true,
    preflight_status: candidate.status,
    invoice_number: candidate.invoice_number || null,
    source_key: sourceKey,
    retained_amount: candidate.retained_amount,
    draft_rate: rate,
    draft_commission_amount: commissionAmount,
    blocker_codes: candidate.blocker_codes,
    potential_advisory_override: Boolean(candidate.advisory_override_review),
    advisory_override_review_required: Boolean(candidate.advisory_override_review),
    invoice_id: candidate.invoice_id || null,
    payment_id: candidate.payment_id || null,
    currency: candidate.currency || "usd",
  };
  const payload: Row = {
    partner_id: candidate.partner_id,
    account_id: candidate.account_id,
    revenue_event_id: eventId,
    plan_id: planId,
    entry_type: "direct",
    eligible_amount: candidate.retained_amount,
    commission_rate: rate,
    commission_amount: commissionAmount,
    status: "pending",
    earned_at: candidate.revenue_date || new Date().toISOString(),
    payable_at: null,
    notes: "Manual commission draft created from Commission Readiness preflight.",
    source_key: sourceKey,
    source_type: "eligible_revenue_event",
    review_status: "pending_review",
    created_by: userData.user?.id || null,
    metadata,
  };
  const inserted = await supabase.from("commission_entries").insert(payload).select("*").single();
  if (inserted.error) {
    if (inserted.error.code === "23505") {
      const retry = await supabase.from("commission_entries").select("*").eq("source_key", sourceKey).limit(1);
      if (retry.data?.[0]) {
        await writeRevenueAudit("commission_draft_reused", "commission_entry", text(retry.data[0] as Row, "id"), null, retry.data[0], "Existing draft commission entry reused after conflict");
        return { data: retry.data[0] as Row, error: null, reused: true };
      }
    }
    return { data: null, error: toPersistenceError(inserted.error, "Could not create draft commission entry") };
  }
  const row = (inserted.data || {}) as Row;
  await writeRevenueAudit("commission_draft_created", "commission_entry", text(row, "id"), null, row, "Manual draft commission entry created");
  return { data: row, error: null, reused: false };
}

function mapOpportunity(row: Row, partnerById: Map<string, string>, accountDirectory: RevenueAccountDirectory = new Map()): RevenueOpportunity {
  const metadata = (row.metadata as Row) || {};
  const accountId = text(row, "account_id") || undefined;
  const account = accountId ? accountDirectory.get(accountId) : undefined;
  const ownershipExpiry = text(metadata, "ownership_expires_at") || undefined;
  return { id: text(row, "id"), partner_id: text(row, "partner_id") || undefined, account_id: accountId, account_name: account ? getAccountDisplayName(account) : accountId ? "Account mapping needs review" : undefined, account_mapping_issue: Boolean(accountId && !account), account_domain: account ? getAccountDomain(account) : undefined, prospect_name: text(row, "referred_entity_name"), website: text(metadata, "website") || undefined, contact_name: text(metadata, "contact_name") || undefined, contact_email: text(metadata, "contact_email") || undefined, partner_name: partnerById.get(text(row, "partner_id")) || undefined, opportunity_type: (text(metadata, "opportunity_type", "new_logo") as RevenueOpportunity["opportunity_type"]), estimated_mrr: number(metadata, "estimated_mrr"), products_of_interest: [], stage: mapOpportunityStage(text(row, "stage")), attribution_scope: text(row, "attribution_scope") as AttributionScope, attribution_review: (text(metadata, "attribution_review", "pending_review") as RevenueOpportunity["attribution_review"]), duplicate_review: text(row, "stage") === "duplicate", notes: text(row, "notes") || text(metadata, "partner_visible_note") || undefined, parent_entity_label: getParentEntityLabel(row, account), source: text(metadata, "source") || undefined, request_type: text(metadata, "request_type") as RevenueOpportunity["request_type"], request_status: text(metadata, "request_status") as RevenueOpportunity["request_status"], submitted_by_user_id: text(metadata, "submitted_by_user_id") || undefined, submitted_by_user_email: text(metadata, "submitted_by_user_email") || undefined, submitted_at: text(metadata, "submitted_at") || undefined, ownership_window_months: number(metadata, "ownership_window_months"), ownership_expires_at: ownershipExpiry, last_meaningful_activity_at: text(metadata, "last_meaningful_activity_at") || undefined, last_meaningful_activity_note: text(metadata, "last_meaningful_activity_note") || undefined, ownership_window_status: (text(metadata, "ownership_window_status") as RevenueOpportunity["ownership_window_status"]) || deriveOwnershipWindowStatus(ownershipExpiry), archived_at: text(metadata, "archived_at") || undefined, archived_by: text(metadata, "archived_by") || undefined, archive_reason: text(metadata, "archive_reason") || undefined, archived_from_stage: text(metadata, "archived_from_stage") || undefined };
}

function getParentEntityLabel(row: Row, account?: Row): string | undefined {
  const rowMetadata = row.metadata && typeof row.metadata === "object" ? row.metadata as Row : {};
  const accountMetadata = account?.metadata && typeof account.metadata === "object" ? account.metadata as Row : {};
  const candidates = [
    row.parent_entity, row.parent_entity_name, row.parent_company, row.parent_company_name,
    row.parent_account_name, row.portfolio_name, row.account_parent,
    rowMetadata.parent_entity, rowMetadata.parent_company,
    account?.parent_entity, account?.parent_entity_name, account?.parent_company,
    account?.parent_company_name, account?.parent_account_name, account?.portfolio_name,
    account?.account_parent, accountMetadata.parent_entity, accountMetadata.parent_company,
  ];
  const value = candidates.find((candidate) => typeof candidate === "string" && candidate.trim());
  return typeof value === "string" ? value.trim().slice(0, 160) : undefined;
}

function mapPartnerType(value: string): RevenuePartner["partner_type"] { return ({ direct_referral: "direct_sales_partner", network_sales: "network_sales_partner", advisory: "advisory_network_partner", implementation: "implementation_partner", agency_wholesale: "agency_wholesale_customer", other: "house_account" } as Record<string, RevenuePartner["partner_type"]>)[value] || "direct_sales_partner"; }
function mapPartnerTypeToDb(value: RevenuePartner["partner_type"]) { return ({ direct_sales_partner: "direct_referral", network_sales_partner: "network_sales", advisory_network_partner: "advisory", implementation_partner: "implementation", agency_wholesale_customer: "agency_wholesale", house_account: "other" })[value]; }
function mapPartnerStatus(value: string): RevenuePartner["status"] { return value === "suspended" ? "payment_hold" : value === "archived" ? "archived" : (value as RevenuePartner["status"]) || "inactive"; }
function mapPartnerStatusToDb(value: RevenuePartner["status"]) { return value === "payment_hold" ? "suspended" : value === "terminated" ? "archived" : value; }
function mapPayoutTerms(value: string): RevenuePartner["payout_terms"] { return (value as RevenuePartner["payout_terms"]) || "net_30_after_collection"; }
function mapOpportunityStage(value: string): RevenueOpportunity["stage"] { return ({ submitted: "submitted", accepted: "accepted", qualified: "under_review", demo: "demo_completed", proposal: "proposal", won: "closed_won", lost: "closed_lost", duplicate: "rejected_duplicate", rejected: "rejected_duplicate" } as Record<string, RevenueOpportunity["stage"]>)[value] || "submitted"; }
function mapOpportunityStageToDb(value: RevenueOpportunity["stage"]) { return ({ submitted: "submitted", under_review: "submitted", accepted: "accepted", demo_scheduled: "demo", demo_completed: "demo", proposal: "proposal", verbal_commit: "proposal", closed_won: "won", closed_lost: "lost", rejected_duplicate: "duplicate" })[value]; }
function mapAttributionStatus(value: string): RevenueAttribution["status"] { return ({ pending: "pending", approved: "active", ended: "superseded", rejected: "rejected" } as Record<string, RevenueAttribution["status"]>)[value] || "pending"; }
function mapAttributionStatusToDb(value: RevenueAttribution["status"]) { return ({ pending: "pending", active: "approved", disputed: "pending", superseded: "ended", rejected: "rejected" })[value]; }
function mapProductCategory(value: string): RevenueProduct["revenue_category"] { return value === "software_onboarding" ? "software_onboarding" : value === "software_subscription" ? "software_subscription" : value === "third_party" ? "third_party" : value === "pass_through" ? "pass_through" : "other_software"; }
function mapPlanType(value: string): RevenueCommissionPlan["plan_type"] { return value === "network_sales" ? "network_partner" : value === "advisory" ? "advisory_override" : (value as RevenueCommissionPlan["plan_type"]) || "custom"; }
function mapPlanTypeToDb(value: RevenueCommissionPlan["plan_type"]) { return ({ direct_referral: "direct_referral", network_partner: "network_sales", advisory_override: "advisory", custom: "other" })[value]; }
function mapCommissionStatus(value: string): RevenueCommissionEntry["status"] { return ({ pending: "pending_review", pending_review: "pending_review", approved: "approved", payable: "payable", paid: "paid", held: "manual_hold", void: "void" } as Record<string, RevenueCommissionEntry["status"]>)[value] || "projected"; }
function mapPayoutStatus(value: string): RevenuePayoutRecord["status"] { return ({ draft: "draft", under_review: "pending_review", approved: "approved", paid: "paid", void: "canceled" } as Record<string, RevenuePayoutRecord["status"]>)[value] || "draft"; }
function mapPayoutStatusToDb(value: RevenuePayoutRecord["status"]) { return ({ draft: "draft", pending_review: "under_review", approved: "approved", paid: "paid", partially_paid: "paid", canceled: "void" })[value]; }
